<template>
  <!-- This component contains the main product modelling grid and its actions. -->
  <v-container class="actions-container pa-2 flex-grow-0">
    <v-row class="ma-0">
      <v-col class="pa-0 d-flex align-center justify-start">
        <div class="mr-1">
          <span class="pr-6 br-1">
            <!-- Page title -->
            <span class="font-weight-bold mr-1">{{ tkey('productModellingTitle') }}</span>

            <!-- Product modelling tooltip -->
            <DocsLink link="toolguide/110-modelling.html" />
            <!-- Run product modelling job -->
            <v-menu>
              <template #activator="{ props }">
                <v-btn
                  id="btnRunProductModelling"
                  primary
                  v-bind="props"
                  :loading="isProductModellingJobRunning"
                  :disabled="isRunProductSetupDisabled"
                  append-icon="mdi-menu-down"
                  class="ml-2"
                >
                  {{ tkey('runProductModelling') }}
                  <template #append>
                    <v-icon color="white" />
                  </template>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="(item, index) in optionsToModel"
                  :key="index"
                  :value="index"
                  :disabled="item.disabled"
                >
                  <v-list-item-title
                    :id="`${item.value}ItemOption`"
                    @click="setupProductModelling(item)"
                    >{{ item.title }}</v-list-item-title
                  >
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- Copy decisions -->
            <v-btn
              v-if="isCopyModellingDecisionsEnabled"
              data-id-e2e="btnCopyDecisions"
              primary
              :disabled="
                !canEditModellingPage || isProductModellingJobRunning || !!notModelledProductsCount
              "
              class="ml-2 mr-2"
              @click="() => (state.copyDecisionsModalOpen = true)"
            >
              {{ tkey('copyDecisions') }}
            </v-btn>
          </span>
        </div>
      </v-col>
      <!-- Product search bar -->
      <v-col class="pa-0 flex-grow-0 justify-end">
        <!-- Filters and actions -->
        <v-col class="d-flex align-center justify-end pa-0">
          <v-btn
            class="outlined-btn mr-2"
            variant="flat"
            secondary
            :disabled="isClearFiltersDisabled"
            @click="clearFilters"
          >
            {{ $t('actions.clearAllFilters') }}
          </v-btn>
          <ProductModellingBulkApplyTrend
            class="mr-3"
            :min-trend-value="MIN_TREND_VALUE"
            @apply-trend="applyTrend"
          />
          <RtlsSearch
            v-model="state.searchString"
            :placeholder="tkey('searchPlaceholder')"
            :disabled="isProductModellingJobRunning"
            class="mr-3"
            @update:model-value="onFilterChanged"
          />
          <ProductModellingViewToggle
            v-if="isProductModellingToggleProductViewsEnabled"
            :selected-view="state.selectedView"
            :product-modelling-views-toggle-type="productModellingViewsToggleType"
            @update-view="updateView"
          />
        </v-col>
      </v-col>
    </v-row>
  </v-container>
  <!-- Alerts count - shows how many products are not modelled -->
  <v-container
    v-show="notModelledProductsCount || shouldShowErrorControls || shouldShowWarningControls"
    class="actions-container pa-2 flex-grow-0"
  >
    <div v-if="notModelledProductsCount">
      <ProductModellingCountsAlert
        :not-modelled-products-count="notModelledProductsCount"
        :total-parent-products-count="totalParentProductsCount"
        :established-products-count="establishedProductsCount"
      />
    </div>

    <!-- Warnings and errors -->
    <ProductModellingWarnings
      :show-error-controls="shouldShowErrorControls"
      :show-warning-controls="shouldShowWarningControls"
      :filter-invalid-rows="state.showInvalidRows"
      :filter-warning-rows="state.showWarningRows"
      :needs-c-d-t="needsCDT"
      :not-modelled-products-count="notModelledProductsCount"
      @toggle-invalid-rows="toggleInvalidRows"
      @toggle-warning-rows="toggleWarningRows"
    />
  </v-container>
  <!-- Main grid content -->
  <div class="h-100 flex-grow-1">
    <AgGridVue
      v-model="gridState.productsData"
      data-testid="grid"
      class="w-100 h-100"
      :loading="gridState.isGridLoading || isProductModellingJobRunning"
      :grid-options="gridOptions"
      :does-external-filter-pass="doesExternalFilterPass"
      :stop-editing-when-cells-loses-focus="true"
      @cell-value-changed="handleCellEdit"
      @grid-ready="onGridReady"
      @drag-stopped="saveColumnState"
      @column-visible="saveColumnState"
    />
  </div>

  <!-- Page actions - save / discard / run calculations -->
  <PageActions
    :has-data-changes="!isEmpty(gridState.currentStateDiff)"
    :has-data-errors="!!size(gridState.validationErrors)"
    :save-disabled="isSaveDisabled"
    :save-loading="state.isSaving"
    :is-discard-enabled="canEditModellingPage"
    :show-export="true"
    :export-options="exportOptions"
    save-btn-text="save"
    @discard="discardChanges"
    @save="onSave(false)"
    @export-product-modelling="onExport"
    @export-sister-products="exportSisterProducts"
  />

  <!-- Copy decisions modal -->
  <CopyModellingDecisionsModal
    ref="copyDecisionsModal"
    :value="state.copyDecisionsModalOpen"
    :is-processing="state.isProcessingCopyDecisions"
    :filtered-product-display-keys="getFilteredProductDisplayKeys()"
    @cancel="() => (state.copyDecisionsModalOpen = false)"
    @confirm="() => (state.isProcessingCopyDecisions = true)"
    @close="
      () => {
        state.copyDecisionsModalOpen = false;
        state.isProcessingCopyDecisions = false;
      }
    "
    @diff-change="updateDiffFromCopyDecisions"
  />

  <!-- Dependency tree modal -->
  <DependencyTreeFeedbackModal
    :value="state.dependencyTreeModalOpen"
    :results="state.dependencyTreeFeedback"
    page="productModelling"
    :save-btn-text="
      !state.hasOnlyAssortmentFlagModified ? 'proceedWithDeleting' : 'confirmDownstreamChanges'
    "
    proceed-btn-text="proceedWithoutDeleting"
    :warning-message="dependencyTreeWarningMessage"
    :able-to-proceed-without-deleting="!state.hasOnlyAssortmentFlagModified"
    @close="closeDependencyTreeModal()"
    @proceed="proceedWithoutDependencyTreeChanges()"
    @commit="commitDependencyTreeChanges()"
  />
  <v-snackbar v-model="state.isWarningSnackbarOpen" multi-line>
    <span>{{ state.snackbarWarnings }}</span>
    <template #actions>
      <v-btn @click="state.isWarningSnackbarOpen = false">{{ $t('actions.close') }}</v-btn>
    </template>
  </v-snackbar>
</template>

<script setup>
import to from 'await-to-js';
import { reactive, computed, onMounted, watch } from 'vue';
import {
  cloneDeep,
  each,
  filter,
  get,
  isEmpty,
  isNull,
  isNil,
  isNumber,
  isEqual,
  keyBy,
  map,
  merge,
  pick,
  reduce,
  set,
  size,
  some,
  values,
  isUndefined,
  intersection,
  toNumber,
  round,
  orderBy,
  has,
  forEach,
  setWith,
  transform,
  omit,
} from 'lodash';
import dayjs from 'dayjs';

// composables
import { useFeature } from '@/composables/use-feature';
import { usePermission } from '@/composables/use-permission';
import { useInternationalization } from '@/composables/use-internationalization';
import { useRouter, useRoute } from 'vue-router';
import { useProductIndicators } from '@/composables/product-indicators';
import { useFormatNumber } from '@/composables/use-format-number';
import { useScenarioStatusFormatter } from '@/composables/use-scenario-status-formatter';
import { useAiAgentActions } from '@/composables/use-ai-agent-actions';
import { setGridApiForTesting } from '@/composables/use-grid-api-for-testing';

// stores
import { useClusteringStore } from '@/stores/clustering';
import { useScenariosStore } from '@/stores/scenarios';
import { useScenarioProductsStore } from '@/stores/scenario-products';
import { useSnackbarStore } from '@/stores/snackbar';
import { useDependencyTreeStore } from '@/stores/dependency-tree';
import { useContextStore } from '@/stores/context';
import { useFurnitureStore } from '@/stores/furniture';
import { useAiStore } from '@/stores/ai';

// utils and helpers
import agGridUtils from '@/utils/ag-grid-utils';
import numberUtils from '@/utils/number-format-utils';
import productUtils from '@/utils/product-utils';
import rosUtils from '@sharedModules/data/utils/ros-utils';
import { jobApi } from '@/helpers';
import gridPreferences from '@/utils/grid-preferences';
import modellingUtils from '@/utils/modelling-utils';
import cannGroupUtils from '@/utils/cann-groups';

// enums
import modellingTypeOptions from '@enums/product-modelling-types';
import sectionStatuses from '@enums/section-status';
import originSourceTypes from '@enums/origin-source-types';

// other components
import AgGridButtonRendererEdit from '@/components/AgGridButtonRendererEdit.vue';
import IconRenderer from '@/components/IconRenderer.vue';
import { useProductModellingGridHelpers } from '../composables/product-modelling-grid-helpers';
import { useProductModelling } from '../composables/product-modelling';

const MIN_TREND_VALUE = -100;
const GRID_ROW_HEIGHT = 30;
const LOCALIZATION_KEY = 'productModellingPage';
const OVERVIEW_COLUMN_STATE_PREFIX = 'productModellingOverviewColumnState';
const OVERVIEW_COLUMN_STATE_VERSION = 2;

const contextStore = useContextStore();
const clusteringStore = useClusteringStore();
const scenariosStore = useScenariosStore();
const scenarioProductsStore = useScenarioProductsStore();
const snackbarStore = useSnackbarStore();
const dependencyTreeStore = useDependencyTreeStore();
const furnitureStore = useFurnitureStore();
const route = useRoute();
const router = useRouter();

const {
  isProductModellingToggleProductViewsEnabled,
  isCopyModellingDecisionsEnabled,
  isTotalSalesOverrideEnabled,
  isVolumeRoSEnabled,
  showProductModellingForNewProductsOnly,
  productModellingViewsToggleType,
  hasClientProductKeyColumnEnabled,
  historicKPIsEnabled,
  useMarkdownAndWasteToCalculateMarginAndPriceEnabled,
  productModellingAlertsCriteria,
  modellingMainPageAllowedColumns,
  defaultModellingViewToAllProducts,
} = useFeature();

const { formatScenarioProgress } = useScenarioStatusFormatter('product-modelling');
/* eslint-disable no-use-before-define */
useAiAgentActions({
  allowScenarioWorkflowNavigation: true,
  getExclude: () =>
    canEditModellingPage
      ? []
      : [
          'run-product-modelling',
          'apply-bulk-trend',
          'save-modelling-changes',
          'discard-modelling-changes',
        ],
  actions: {
    'run-product-modelling': {
      description:
        'Trigger the product modelling job for this scenario. ' +
        'Use scope "allProducts" for the first-ever run or a full refresh of all products. ' +
        'Use scope "notModelled" to run only for products not yet modelled (only valid once at least one product has been modelled). ' +
        'Do not call this if the CDT has not been run or if a job is already running. ' +
        'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
      workingLabel: 'Running product modelling',
      confirmationLabel: args =>
        `run product modelling for ${args.scope === 'notModelled' ? 'not-yet-modelled products only' : 'all products'}`,
      parameters: {
        type: 'object',
        properties: {
          scope: {
            type: 'string',
            enum: ['allProducts', 'notModelled'],
            description:
              '"allProducts" to model everything; "notModelled" to model only products not yet modelled.',
          },
        },
        required: ['scope'],
      },
      handler: async ({ scope }) => {
        if (!canEditModellingPage)
          return '[Tool] Action failed: you do not have permission to run product modelling. Inform the user.';
        if (isProductModellingJobRunning.value)
          return '[Tool] Action failed: a product modelling job is already running. Ask the user to wait for it to finish.';
        if (needsCDT.value)
          return '[Tool] Action failed: the Category Decision Tree (CDT) must be run in the Clustering section before product modelling can be triggered. Inform the user.';
        if (!state.canRunProductSetup)
          return '[Tool] Action failed: product modelling setup is not available right now. Ask the user to try again shortly.';
        if (scope !== 'allProducts' && scope !== 'notModelled')
          return `[Tool] Action failed: invalid scope "${scope}". Must be "allProducts" or "notModelled". Ask the user to clarify.`;
        const hadModelledBefore = some(gridState.productsData, p => p.hasBeenModelled);
        if (scope === 'notModelled' && !hadModelledBefore)
          return '[Tool] Action failed: "notModelled" scope is only available after the first full modelling run. Use "allProducts" instead.';
        if (scope === 'notModelled' && notModelledProductsCount.value < 1)
          return '[Tool] Action failed: there are no unmodelled products to model. All products have already been modelled.';
        await setupProductModelling({ value: scope });
        if (state.dependencyTreeModalOpen)
          return '[Tool] Action paused: triggering product modelling detected that downstream work (such as switching matrices or CDTs) will be affected. A confirmation modal is now open on screen. The user must review it and confirm or cancel before the job can proceed. Let the user know and ask them to action the modal.';
        return `[Tool] Action succeeded: product modelling job triggered for ${scope === 'notModelled' ? 'not-yet-modelled products' : 'all products'}. The job is running in the background — results will appear in the grid when it completes.`;
      },
    },
    'apply-bulk-trend': {
      description:
        'Apply the same trend percentage to all currently visible (filtered) products. ' +
        'Products that already have a Total Sales Override are automatically skipped. ' +
        'Positive values increase predictions; negative values decrease them. ' +
        'Do not save automatically after applying — ask the user first. ' +
        'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
      workingLabel: 'Applying bulk trend',
      confirmationLabel: args =>
        `apply a ${args.trendValue > 0 ? '+' : ''}${args.trendValue}% trend to all visible products`,
      parameters: {
        type: 'object',
        properties: {
          trendValue: {
            type: 'number',
            description: 'The trend percentage to apply (e.g. 10 for +10%, -15 for -15%).',
          },
        },
        required: ['trendValue'],
      },
      handler: async ({ trendValue }) => {
        if (!canEditModellingPage)
          return '[Tool] Action failed: you do not have permission to edit product modelling data. Inform the user.';
        if (isProductModellingJobRunning.value)
          return '[Tool] Action failed: a product modelling job is currently running. Ask the user to wait for it to finish before applying trends.';
        const parsed = Number(trendValue);
        if (Number.isNaN(parsed))
          return `[Tool] Action failed: "${trendValue}" is not a valid number. Ask the user for a numeric trend value.`;
        const diffSizeBefore = size(gridState.currentStateDiff);
        applyTrend(parsed);
        const diffSizeAfter = size(gridState.currentStateDiff);
        if (diffSizeAfter === diffSizeBefore && parsed !== 0)
          return '[Tool] Action completed with no changes: no eligible products were updated. All visible products may already have a Total Sales Override set, which skips bulk trend application. Inform the user.';
        const updatedCount = diffSizeAfter - diffSizeBefore;
        return `[Tool] Action succeeded: a trend of ${parsed > 0 ? '+' : ''}${parsed}% applied to ${updatedCount > 0 ? updatedCount : 'visible eligible'} product(s). Changes are unsaved — ask the user if they want to save.`;
      },
    },
    'save-modelling-changes': {
      description:
        'Save all current product modelling changes (sister products, overrides, trends) to the database. ' +
        'IMPORTANT: Do NOT save automatically after running a job or applying a trend. ' +
        'Only call this when: (1) the user explicitly asks to save, ' +
        '(2) the user uses language like "go ahead", "apply", "save it", or "just do it", ' +
        'or (3) you have completed ALL steps of an explicitly user-approved multi-step plan and saving was part of the agreed plan. ' +
        'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
      workingLabel: 'Saving changes',
      confirmationLabel: 'save all product modelling changes',
      parameters: { type: 'object', properties: {}, required: [] },
      handler: async () => {
        if (!canEditModellingPage)
          return '[Tool] Action failed: you do not have permission to save product modelling changes. Inform the user.';
        if (isSaveDisabled.value)
          return '[Tool] Action failed: save is currently disabled (a job may be running, or there are validation errors). Ask the user to resolve any issues first.';
        if (isEmpty(gridState.currentStateDiff))
          return '[Tool] Action failed: there are no unsaved changes to save. Inform the user.';
        await onSave();
        if (state.dependencyTreeModalOpen)
          return '[Tool] Action paused: saving detected that downstream work (such as switching analysis or CDTs) will be affected by these changes. A confirmation modal is now open on screen listing what will be reset. The user must review it and either confirm or cancel before the save completes. Let the user know and ask them to action the modal.';
        if (!isEmpty(gridState.currentStateDiff))
          return '[Tool] Action failed: the save did not complete — changes are still pending. Inform the user and suggest they check for validation errors or try again.';
        return '[Tool] Action succeeded: product modelling changes saved.';
      },
    },
    'discard-modelling-changes': {
      description:
        'Discard all unsaved product modelling changes, reverting to the last saved state. ' +
        'Only call this when the user explicitly asks to discard, undo, revert, or cancel their changes. ' +
        'Do not call this proactively. ' +
        'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
      workingLabel: 'Discarding changes',
      confirmationLabel: 'discard all unsaved product modelling changes',
      parameters: { type: 'object', properties: {}, required: [] },
      handler: async () => {
        if (!canEditModellingPage)
          return '[Tool] Action failed: you do not have permission to edit product modelling data. Inform the user.';
        if (isEmpty(gridState.currentStateDiff))
          return '[Tool] Action failed: there are no unsaved changes to discard. Inform the user.';
        discardChanges();
        if (!isEmpty(gridState.currentStateDiff))
          return '[Tool] Action failed: the discard did not fully revert the changes. Inform the user and suggest they refresh the page.';
        return '[Tool] Action succeeded: all unsaved product modelling changes have been discarded.';
      },
    },
  },
});
/* eslint-enable no-use-before-define */
const aiStore = useAiStore();

const useMarkdownAndWasteToCalculateMarginAndPrice = computed(
  () =>
    useMarkdownAndWasteToCalculateMarginAndPriceEnabled &&
    scenariosStore.getUseMarkdownAndWasteToCalculateMarginAndPrice
);

const { canEditModellingPage } = usePermission();

const { t, tkey } = useInternationalization(LOCALIZATION_KEY);

const { fetchData } = useProductModelling();
const {
  overrideValueFormatter,
  salesConfidenceScoreRenderer,
  tooltipValueGetter,
  unsetCurrentStateDataDiff,
} = useProductModellingGridHelpers();

const { getProductType } = useProductIndicators();
const { getRoundedPercentage, formatTrendApplied, formatNumberWithOneDecimal } = useFormatNumber();

const { roundFormatter, percentageFormatter, datesFormatter } = agGridUtils.formatters;
const { getNumericFilterParams } = agGridUtils.utils;
const { valueIsNotNumericPermissive, valueIsLess, valueIsLessOrEqual } = agGridUtils.validations;

const { stringToNumberIfNumeric } = numberUtils;

let initialProductsData = [];
let leafCannGroups = [];

// General component state
const state = reactive({
  searchString: '',
  selectedView: 'nonEstablishedProductsOnly',
  copyDecisionsModalOpen: false,
  isProcessingCopyDecisions: false,
  dependencyTreeModalOpen: false,
  dependencyTreeFeedback: {},
  hasOnlyAssortmentFlagModified: false,
  calledFromInitSetupProductModelling: false,
  invalidRows: {},
  warningRows: {},
  showInvalidRows: false,
  showWarningRows: false,
  selectedModellingOption: null,
  filterModel: null,
  productsRoSPercentileMap: {},
  isWarningSnackbarOpen: false,
  snackbarWarnings: '',
  isSaving: false,
  canRunProductSetup: true,
});

// Grid state
const gridState = reactive({
  isGridLoading: false,
  currentStateDiff: {},
  validationErrors: {},
  productsData: [],
  existingProducts: [],
  changedSinceCalculation: new Set(),
  initialSavedState: null,
  initialKeyIdMap: {},
});

let gridApi = null;

// Grid options
const gridOptions = computed(() => ({
  suppressContextMenu: true,
  tooltipShowDelay: 0,
  rowHeight: GRID_ROW_HEIGHT,
  groupHeaderHeight: 30,
  columnDefs: [],
  undoRedoCellEditing: true,
  enableRangeSelection: true,
  cellSelection: { handle: { mode: 'fill' } },
  stopEditingWhenCellsLoseFocus: true,
  // TODO There's a lot of edge cases we need to handle when dealing with formatters / value setters / casting.
  // We should consider moving this logic more globally (for example to agGridUtils).
  processCellFromClipboard: params => {
    // Special handling for percentage columns when it's the source
    if (params.value && typeof params.value === 'string' && params.value.includes('%')) {
      // We have a percentage value being pasted
      const cleanValue = params.value.replace(/%/g, '');

      if (params.column.colId === 'trendApplied') {
        // When pasting into trendApplied, keep as is
        return isNil(cleanValue) ? null : Number(cleanValue);
      }

      if (
        params.column.colId === 'totalSalesOverride' ||
        params.column.colId === 'rateOfSaleOverride'
      ) {
        // When pasting a percentage value into non-percentage fields,
        // interpret it as the actual number, not as a decimal
        return isNil(cleanValue) ? null : Number(cleanValue);
      }

      if (
        params.column.colId.includes('totalMarkdownAssortmentOverridePercentage') ||
        params.column.colId.includes('wasteAssortmentOverridePercentage')
      ) {
        // stringToNumberIfNumeric will correctly handle converting localised decimal places (e.g. 2,5 -> 2.5)
        return isNil(cleanValue) ? null : stringToNumberIfNumeric(cleanValue);
      }
    }

    // Default handling for other columns
    return agGridUtils.utils.processCellFromClipboard(params);
  },
  defaultColDef: {
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    ...agGridUtils.colDefs.default,
  },
  defaultColGroupDef: {
    marryChildren: true,
    openByDefault: true,
  },
  // Enables the tool panel on RHS of the grid
  sideBar: {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        minWidth: 225,
        maxWidth: 225,
        width: 225,
        toolPanelParams: {
          suppressPivotMode: true,
          suppressPivots: true,
          suppressRowGroups: true,
          suppressValues: true,
        },
      },
    ],
  },
  columnTypes: { numericAlignment: agGridUtils.columnTypes.numericAlignment },
  isExternalFilterPresent: () => true,
  getRowId: params => params.data.productKey.toString(), // use productKey as row id
  overlayLoadingTemplate: `<span class="ag-overlay-loading-center">${tkey(
    'messages.inProgress'
  )}</span>`,
  onFilterChanged: () => {
    state.filterModel = gridApi.getFilterModel();
  },
}));

const isClearFiltersDisabled = computed(() => isEmpty(state.filterModel) && !state.searchString);

const dependencyTreeWarningMessage = computed(() => {
  if (
    state.hasOnlyAssortmentFlagModified &&
    state.dependencyTreeFeedback.outdateOptimisedCanvases
  ) {
    return 'dependencyTree.outdatedCanvasesWarning';
  }

  if (state.hasOnlyAssortmentFlagModified) return '';

  return 'dependencyTree.productModellingWarning';
});

const clearFilters = () => {
  state.searchString = '';
  gridApi.setFilterModel(null);
  gridApi.onFilterChanged();
};

const applyDefaultSort = () => {
  const columnsWithSortApplied = filter(gridApi.getColumnState(), 'sort');
  // We want to avoid overriding their preserved settings, if they exist
  if (size(columnsWithSortApplied)) return;

  gridApi.applyColumnState({
    state: [
      {
        colId: 'cannGroupId',
        sort: 'asc',
      },
    ],
  });
};

const onFilterChanged = () => {
  gridApi.onFilterChanged();
};

const setProductsDataToDisplay = view => {
  state.selectedView = view;
  if (gridApi) {
    onFilterChanged();
  }
};

const updateView = value => {
  state.selectedView = value;
  setProductsDataToDisplay(value);
};

const isSemiNewProduct = params =>
  !params.data.isNewProduct && params.data.originSource === originSourceTypes.newInTemplate;

const isNewOrSemiProduct = params => !!params.data.isNewProduct || isSemiNewProduct(params);

const isProductEstablished = (modellingType, salesConfidenceScore) => {
  // if a product has not been modelled yet, derive if it's established from its sales confidence score (should be 1)
  // We don't want to always just check this score, as some products might be established in cluster A but not in cluster B
  if (!modellingType) return salesConfidenceScore === 1;

  // otherwise, use the modelling type
  return modellingType === modellingTypeOptions.modellingTypes.established;
};

const establishedProductsCount = computed(() => {
  return size(
    filter(gridState.productsData, p =>
      isProductEstablished(p.modellingType, p.salesConfidenceScorePerformancePeriod)
    )
  );
});

const notModelledProductsCount = computed(() => {
  // returns size of all products that weren't modelled, including sister products
  const notModelled = size(filter(gridState.productsData, p => !p.hasBeenModelled));

  // subtracting the establishedProductsCount gets rid of the sister products from the total
  const totalNotModelled = notModelled - establishedProductsCount.value;

  return totalNotModelled > 0 ? totalNotModelled : 0;
});

const optionsToModel = computed(() => {
  // Enforces `all products` to be always the only option available when modelling for the first time
  const hadModelledBefore = some(gridState.productsData, p => p.hasBeenModelled);

  return [
    {
      title: tkey('allProducts'),
      value: modellingTypeOptions.productToModelTypes.allProducts,
      disabled: gridState.productsData.length < 1,
    },
    {
      title: tkey('notModelled'),
      value: modellingTypeOptions.productToModelTypes.notModelled,
      disabled: !hadModelledBefore || notModelledProductsCount.value < 1,
    },
  ];
});

const isRowGreyedOut = () => !canEditModellingPage;

const totalParentProductsCount = computed(() => {
  // returns size of all parent products
  return size(gridState.productsData);
});

const isModellingCompleted = computed(() => {
  const modellingStatus = get(scenariosStore.selectedScenarioStatus, 'modelling.status');
  return modellingStatus === 'success';
});

// Getter functions needed for the column definitions
const isIncludedForAssortment = params => {
  return get(params.data, 'assortment', false);
};

const hasBeenModelled = params => {
  // returns true if the product has been modelled
  return get(params.data, 'hasBeenModelled', false);
};

const isEditableRow = params => {
  // can't edit rows that are not modelled
  return canEditModellingPage && hasBeenModelled(params);
};

const showEditAction = params => {
  const modellingCompleted = isModellingCompleted.value;
  const editableRow = isEditableRow(params);
  const includedForAssortment = isIncludedForAssortment(params);
  const productNotEstablished = !isProductEstablished(params.data.modellingType || null);

  // only shows icon after modelling runs and for:
  // not established products, editable and included for assortment
  return modellingCompleted && editableRow && includedForAssortment && productNotEstablished;
};

const isROSGreyedOut = params => {
  const { trendApplied } = params.data;
  // Grey out ROS column if trendApplied is set and not zero
  return isNumber(trendApplied) && trendApplied !== 0;
};

const isRateOfSaleOverrideEditable = params => {
  return (
    isEditableRow(params) &&
    // we don't allow overriding ROS if total sales override is set
    !isNumber(params.data.totalSalesOverride) &&
    // we don't allow overriding ROS for established products
    !isProductEstablished(
      params.data.modellingType,
      params.data.salesConfidenceScorePerformancePeriod
    ) &&
    // we don't allow overriding ROS for products not included in assortment
    isIncludedForAssortment(params) &&
    isNewOrSemiProduct(params)
  );
};

const isMarkdownAndWasteOverrideEditable = params => {
  return (
    isEditableRow(params) &&
    // we don't allow overriding markdown and waste existing products
    // Only new or semi products can use these overrides
    isNewOrSemiProduct(params) &&
    // we don't allow overriding markdown and waste for products not included in assortment
    isIncludedForAssortment(params)
  );
};

const hasSpacebreakKPIs = () => {
  return (
    !isEmpty(furnitureStore.scenarioFurniture.storeClasses) &&
    // some spacebreaks may not have sales, so we check if at least one of them has
    furnitureStore.scenarioFurniture.storeClasses.some(sc =>
      sc.spacebreaks.some(
        sb => isNumber(sb.averageAssortmentSales) && sb.averageAssortmentSales > 0
      )
    )
  );
};

const isTotalSalesOverrideEditable = params => {
  return (
    isEditableRow(params) &&
    // we don't allow overriding total sales for established products
    !isProductEstablished(
      params.data.modellingType,
      params.data.salesConfidenceScorePerformancePeriod
    ) &&
    // we don't allow overriding total sales for products not included in assortment
    isIncludedForAssortment(params) &&
    hasSpacebreakKPIs(params) &&
    // Only new products can use these overrides
    isNewOrSemiProduct(params)
  );
};

const isTrendAppliedEditable = params => {
  const productIsEstablished = isProductEstablished(
    params.data.modellingType,
    params.data.salesConfidenceScorePerformancePeriod
  );

  return (
    // User must be able to edit the page
    canEditModellingPage &&
    // Product is either established or modelled
    (hasBeenModelled(params) || productIsEstablished) &&
    // we don't allow overriding ROS if total sales override is set
    !isNumber(params.data.totalSalesOverride) &&
    // we don't allow overriding trend for products not included in assortment
    isIncludedForAssortment(params)
  );
};

const isForAssortmentEditable = params => {
  const productIsEstablished = isProductEstablished(
    params.data.modellingType,
    params.data.salesConfidenceScorePerformancePeriod
  );
  return (
    // User must be able to edit the page
    canEditModellingPage &&
    // Product is either established or modelled
    (hasBeenModelled(params) || productIsEstablished)
  );
};

const isPlannedSpacebreakEditable = params =>
  isEditableRow(params) &&
  hasSpacebreakKPIs(params) &&
  // Only new products can use these overrides
  isNewOrSemiProduct(params);

const hasDiff = params => {
  const { productKey } = params.data;
  const { field } = params.colDef;
  const path = `${productKey}.${field}`;

  const currentValue = get(params.data, field);
  const originalValue = get(gridState.initialSavedState, path);

  return agGridUtils.comparators.didValueChange(currentValue, originalValue);
};

const calculateMarginAndPriceByMarkdownAndWasteOverride = async params => {
  let selectedClusterKey = '';
  const colId = params.colDef.colId.split('.');
  if (colId.length > 1) selectedClusterKey = `${colId[0]}.`;

  const totalMarkdownAssortmentOverridePercentage = get(
    params.data,
    `${selectedClusterKey}totalMarkdownAssortmentOverridePercentage`,
    null
  );
  const wasteAssortmentOverridePercentage = get(
    params.data,
    `${selectedClusterKey}wasteAssortmentOverridePercentage`,
    null
  );

  const kpisOverrideUpdates = {
    // valueGetter doesnt parse the data object only newValue property
    // and divide by 100 to get the percentage
    totalMarkdownAssortmentOverridePercentage,
    wasteAssortmentOverridePercentage,
  };

  const updates = [
    {
      productKey: params.data.productKey,
      clusterId: selectedClusterKey.length ? colId[0] : null,
      kpisOverrideUpdates,
    },
  ];

  gridState.isGridLoading = true;
  const scenarioId = get(scenariosStore.selectedScenario, '_id', null);
  const [err, updatedMarginAndPrice = []] = await to(
    jobApi.runFunction('update-products-ros', {
      scenario_id: scenarioId,
      updates,
    })
  );
  gridState.isGridLoading = false;

  if (err) {
    snackbarStore.showWarning();
  }

  updatedMarginAndPrice.forEach(clusterUpdate => {
    const clusterPrefix = isNil(clusterUpdate.clusterId) ? '' : `${clusterUpdate.clusterId}.`;

    forEach(clusterUpdate, (value, field) => {
      // Avoid changing other fields that might comeback
      if (['margin', 'price'].includes(field)) {
        params.node.setDataValue(`${clusterPrefix}${field}`, value);
      }
    });
  });
};

const calculateClusteredProductsRateOfSaleOverride = async params => {
  const updates = [
    {
      productKey: params.data.productKey,
      // In case we clear the RoS override value we need to recalcute the RoS
      // with the original value for the clusters
      rateOfSaleOverride: params.data.rateOfSaleOverride || params.data.rateOfSale,
    },
  ];

  gridState.isGridLoading = true;
  const scenarioId = get(scenariosStore.selectedScenario, '_id', null);
  const [err, updatedRateOfSale = []] = await to(
    jobApi.runFunction('update-products-ros', {
      scenario_id: scenarioId,
      updates,
    })
  );
  gridState.isGridLoading = false;

  if (err) {
    snackbarStore.showWarning();
  }

  updatedRateOfSale.forEach(clusterUpdate => {
    const clusterPrefix = isNil(clusterUpdate.clusterId) ? '' : `${clusterUpdate.clusterId}.`;

    forEach(clusterUpdate, (value, field) => {
      // Avoid changing other fields that might comeback
      if (field === 'rateOfSale') {
        params.node.setDataValue(`${clusterPrefix}${field}`, value);
      }
    });
  });
};

const getGridRowOrder = () => {
  const rowIds = [];
  gridApi.forEachNodeAfterFilterAndSort(node => {
    rowIds.push(node.data.productKeyDisplay); // Assuming your data has an 'id' field
  });

  return rowIds;
};

const goToProductDetails = params => {
  const { productKeyDisplay } = params.data;

  scenarioProductsStore.overviewProductOrder = getGridRowOrder();

  router.push({
    name: 'product-detail',
    query: { ...route.query },
    params: { ...route.params, productKeyDisplay },
  });
};

const calculateRoS = params => {
  return rosUtils.calculateFinalRoS({
    measuredRos: params.data.rateOfSale,
    rosOverride: params.data.rateOfSaleOverride,
    trendApplied: params.data.trendApplied,
  });
};

const calculateAllFinalRosPercentile = productsData => {
  let products = [];
  state.productsRoSPercentileMap = {};
  if (gridApi) {
    gridApi.forEachNode(node => products.push(node.data));
  }

  if (productsData) {
    products = productsData;
  }

  const productsIncludedAssortment = filter(products, 'assortment');
  const productsFinalRoSCalculated = map(productsIncludedAssortment, p => {
    // We need to use each product's final RoS for the calculation
    const finalRoS = calculateRoS({ data: p });
    return {
      ...p,
      finalRoS,
    };
  });

  const len = productsFinalRoSCalculated.length;
  const sortedData = orderBy(productsFinalRoSCalculated, 'finalRoS', 'asc');
  const percentiles = {};

  sortedData.forEach(({ finalRoS }, i) => {
    const percentile = ((1 + i) / len) * 100;
    percentiles[finalRoS] = round(percentile, 2);
  });

  // Already map the productKey to the finalRoS percentile to avoid more finalRoS calculations
  productsFinalRoSCalculated.forEach(product => {
    state.productsRoSPercentileMap[product.productKey] = percentiles[product.finalRoS];
  });
};

const spacebreaksByStoreClassId = computed(() => {
  return transform(
    furnitureStore.scenarioFurniture.storeClasses,
    (acc, sc) => {
      acc[sc._id] = keyBy(sc.spacebreaks, '_id');
    },
    {}
  );
});

function getSpacebreakNameById(params) {
  const eligibleStoreClass = params.data.storeClassEligibility.find(sc => sc.value).id;
  if (!spacebreaksByStoreClassId.value[eligibleStoreClass][params.data.plannedSpacebreakId])
    return '';

  return spacebreaksByStoreClassId.value[eligibleStoreClass][params.data.plannedSpacebreakId].name;
}

const getProductPlannedSpacebreak = (params, options = {}) => {
  const eligibleStoreClass = params.data.storeClassEligibility.find(sc => sc.value);

  if (!eligibleStoreClass) return {};

  const spacebreaksInStoreClass = spacebreaksByStoreClassId.value[eligibleStoreClass.id];
  let plannedSpacebreak = params.data.plannedSpacebreakId
    ? spacebreaksInStoreClass[params.data.plannedSpacebreakId]
    : null;

  if (!plannedSpacebreak && options.shouldSetFirstAvailableSpacebreak) {
    const firstEligibleSpacebreakId = Object.keys(spacebreaksInStoreClass)[0];
    plannedSpacebreak = spacebreaksInStoreClass[firstEligibleSpacebreakId];
  }

  return { eligibleStoreClass, spacebreaksInStoreClass, plannedSpacebreak };
};

// If there's a total sales override, calculate new rate of sale override based on formula below.
const setNewRoSOverride = params => {
  // If total sales override has been set, update rate of sale override and clear trend
  const hasTotalSalesOverride = isNumber(params.data.totalSalesOverride); // TSO
  const hasPlannedSpacebreak = !isEmpty(params.data.plannedSpacebreakId); // PS

  if (hasPlannedSpacebreak && !hasTotalSalesOverride) {
    // when user just picked a planned spacebreak, set the total sales override to 0
    params.node.setDataValue('totalSalesOverride', 0);
  }
  // In we the user wipes the selected planned sb, we should not set it back to te first available
  const shouldSetFirstAvailableSpacebreak =
    hasTotalSalesOverride && params.data.totalSalesOverride > 0 && !hasPlannedSpacebreak;
  const userUnselectedSpacebreak =
    params.colDef.colId === 'plannedSpacebreakId' &&
    params.newValue === null &&
    size(params.oldValue);
  const { plannedSpacebreak } = getProductPlannedSpacebreak(params, {
    shouldSetFirstAvailableSpacebreak,
  });

  if (shouldSetFirstAvailableSpacebreak) {
    // when user just inputted an override, we pick the fist eligible spacebreak
    params.node.setDataValue('plannedSpacebreakId', plannedSpacebreak);
  }

  if (userUnselectedSpacebreak) {
    params.node.setDataValue('totalSalesOverride', null);
  }
  const hasSalesInPlannedSpacebreak =
    plannedSpacebreak && plannedSpacebreak.averageAssortmentSales > 0;

  if (plannedSpacebreak && !hasSalesInPlannedSpacebreak && hasTotalSalesOverride) {
    /** If the spacebreak the user plans to put the product into has no AVG sales,
     * we cap it to 0.
     */
    params.node.setDataValue('rateOfSaleOverride', 0);
  } else if (plannedSpacebreak && hasSalesInPlannedSpacebreak && hasTotalSalesOverride) {
    // Calculate rate of sale override as total sales * average store scope of all products / total sales in spacebreak
    const newRoSOverride = round(
      (params.data.totalSalesOverride * scenariosStore.selectedScenario.averageScopeSales) /
        plannedSpacebreak.totalSalesSpacebreak,
      6
    );

    params.node.setDataValue('rateOfSaleOverride', newRoSOverride);
  }

  // if user inputs for the first time either TSO or PS, we
  if (!params.oldValue && params.data.trendApplied) {
    params.node.setDataValue('trendApplied', null);
    state.isWarningSnackbarOpen = true;
    state.snackbarWarnings = tkey('tooltips.cannotSetWithTotalSales');
  }
};

const getAvailableSpacebreaksForProduct = params => {
  const { eligibleStoreClass, spacebreaksInStoreClass } = getProductPlannedSpacebreak(params);

  const cellParamsOptions = {
    values: [],
    allowTyping: true,
    filterList: true,
    highlightMatch: true,
  };

  if (!eligibleStoreClass) return cellParamsOptions;

  return {
    // Append an empty value
    values: [null, ...values(spacebreaksInStoreClass)],
    allowTyping: true,
    filterList: true,
    highlightMatch: true,
    formatValue: value => {
      // empty when nothing is selected
      if (!value || !spacebreaksInStoreClass[get(value, '_id', value)]) return '';

      return spacebreaksInStoreClass[get(value, '_id', value)].name;
    },
  };
};

function resetTotalSalesOverride(params) {
  // If total sales override is removed, remove rate of sale override and revert trend to original server value
  params.node.setDataValue('rateOfSaleOverride', null);
  params.node.setDataValue('plannedSpacebreakId', null);
  // Get original trend value from server data
  const originalTrendValue = get(
    gridState.initialSavedState,
    `${params.data.productKey}.trendApplied`
  );
  params.node.setDataValue('trendApplied', originalTrendValue);

  calculateAllFinalRosPercentile();
}

const headers = {
  completionCheckbox: {
    headerName: tkey('️completed'),
    headerTooltip: tkey('headerTooltips.markAsComplete'),
    field: 'modellingMarkedComplete',
    colId: 'modellingMarkedComplete',
    pinned: 'left',
    cellDataType: 'boolean',
    suppressHeaderMenuButton: true,
    resizable: false,
    lockPosition: true,
    minWidth: 30,
    maxWidth: 30,
    editable: true,
    headerClass: 'editable-column-header',
    menuTabs: [],
    cellClassRules: {
      'diff-background': hasDiff,
      // hide checkbox for established products, as we don't allow going to edit mode for them
      'd-none': params => {
        if (!isIncludedForAssortment(params)) return true;
        return params.data.modellingType === modellingTypeOptions.modellingTypes.established;
      },
    },
  },
  editIcon: {
    field: 'editAction',
    colId: 'editAction',
    headerName: tkey('edit'),
    headerTooltip: tkey('headerTooltips.edit'),
    minWidth: 30,
    maxWidth: 30,
    pinned: 'left',
    lockPosition: true,
    sortable: false,
    resizable: false,
    suppressHeaderMenuButton: true,
    menuTabs: [],
    cellRenderer: AgGridButtonRendererEdit,
    cellRendererParams: params => ({
      onClick: () => goToProductDetails(params),
      visible: showEditAction(params),
    }),
  },
  productModellingAlerts: {
    headerName: tkey('productModellingAlertsHeader'),
    headerTooltip: tkey('headerTooltips.productModellingAlerts'),
    field: 'productModellingAlerts',
    colId: 'productModellingAlerts',
    minWidth: 35,
    maxWidth: 35,
    pinned: 'left',
    lockPosition: true,
    sortable: false,
    resizable: false,
    suppressHeaderMenuButton: true,
    menuTabs: [],
    valueGetter: params =>
      modellingUtils.getProductModellingAlertsTooltip({ product: params.data }), // Only required to ensure the exported CSV has a value.
    cellRenderer: IconRenderer,
    cellRendererParams: params => ({
      messages: modellingUtils.getProductModellingAlertsTooltip({ product: params.data }),
      hide: !size(modellingUtils.getProductModellingAlertsTooltip({ product: params.data })),
      icon: 'mdi-alert-circle-outline',
      color: 'warning',
    }),
    cellClass: 'd-flex align-center justify-center',
  },
  copiedDecisionsFlag: {
    headerName: tkey('hasCopiedModellingDecisionsHeader'),
    headerTooltip: tkey('headerTooltips.copiedDecisions'),
    field: 'hasCopiedModellingDecisions',
    colId: 'hasCopiedModellingDecisions',
    minWidth: 35,
    maxWidth: 35,
    pinned: 'left',
    lockPosition: true,
    sortable: false,
    resizable: false,
    suppressHeaderMenuButton: true,
    menuTabs: [],
    cellRenderer: IconRenderer,
    cellRendererParams: params => ({
      hide: !params.data.hasCopiedModellingDecisions,
      icon: 'mdi-import',
      color: 'primary',
      messages: [tkey('copiedDecisionsTooltip')],
    }),
    cellClass: 'd-flex align-center justify-center',
  },
  productType: {
    field: 'isNewProduct',
    colId: 'isNewProduct',
    headerName: tkey('type'),
    pinned: 'left',
    minWidth: 70,
    maxWidth: 70,
    resizable: true,
    suppressHeaderMenuButton: true,
    menuTabs: [],
    valueGetter: params => {
      return getProductType(params.data);
    },
    filterValueGetter: params => {
      // Ensure users can filter on the actual product type text - e.g. "EXIST", "NEW"
      return tkey(getProductType(params.data).translation);
    },
    cellRenderer: params => {
      const productType = params.value;
      return `
        <span class="rtls-chip ${productType.style}">
          <span class="rtls-chip__content">
            ${tkey(productType.translation)}
          </span>
        </span>
      `;
    },
  },
  productKey: {
    field: 'productKeyDisplay',
    colId: 'productKey',
    headerName: tkey('productKeyDisplay'),
    type: ['numericColumnCustom', 'numericAlignment'],
    width: 105,
    filter: 'agTextColumnFilter',
    filterParams: agGridUtils.filters.standardStringFilter,
    pinned: 'left',
    cellClassRules: { 'text-disabled': !isEditableRow || notModelledProductsCount.value },
  },
  itemDescription: {
    field: 'itemDescription',
    colId: 'itemDescription',
    headerName: tkey('productDescription'),
    filter: 'agTextColumnFilter',
    pinned: 'left',
    width: 300,
    flex: 1,
    filterParams: agGridUtils.filters.standardStringFilter,
    cellClassRules: { 'text-disabled': !isEditableRow },
  },
  clientProductKey: {
    field: 'clientProductKey',
    colId: 'clientProductKey',
    headerName: t('scenarioInputPage.attributes.clientProductKey'),
    type: ['numericColumnCustom', 'numericAlignment'],
    filter: 'agTextColumnFilter',
    width: 120,
    hide: !hasClientProductKeyColumnEnabled.value,
    filterParams: agGridUtils.filters.standardStringFilter,
    cellClassRules: { 'text-disabled': !isEditableRow },
  },
  combinedUnitAndMeasure: {
    colId: 'combinedUnitAndMeasure',
    field: 'combinedUnitAndMeasure',
    headerName: tkey('combinedContentAndUnit'),
    type: ['numericColumnCustom', 'numericAlignment'],
    filter: 'agTextColumnFilter',
    columnGroupShow: 'open',
    filterParams: agGridUtils.filters.standardStringFilter,
    width: 110,
    valueGetter(params) {
      return (params.data.contentValue || '') + (params.data.contentUnitOfMeasure || '');
    },
    cellClassRules: { 'text-disabled': !isEditableRow },
  },
  cannGroup: {
    field: 'cannGroupId',
    colId: 'cannGroupId',
    columnGroupShow: 'open',
    headerName: t('scenarioCannGroups.nameSingular'),
    width: 120,
    cellClassRules: { 'text-disabled': !isEditableRow },
    filterParams: agGridUtils.filters.standardStringFilter,
    valueGetter: params => {
      return leafCannGroups[params.data.cannGroupId].name;
    },
  },
  assortment: {
    field: 'assortment',
    colId: 'assortment',
    headerName: tkey('assortment'),
    headerTooltip: tkey('headerTooltips.forAssortment'),
    width: 130,
    columnGroupShow: 'open',
    cellRenderer: 'agCheckboxCellRenderer',
    cellEditor: 'agCheckboxCellEditor',
    cellClassRules: { 'diff-background': hasDiff },
    boolean: true,
    singleClickEdit: false,
    headerClass: 'editable-column-header',
    editable: isForAssortmentEditable,
    onCellValueChanged: params => {
      // If the value in the input is not valid the data is not changed so no need to cleanup
      if (!params.newValue) {
        params.node.setDataValue('rateOfSaleOverride', null);
        params.node.setDataValue('totalSalesOverride', null);
        params.node.setDataValue('trendApplied', null);
        // TODO: Check this for the planned spacebreak
      }

      // Refresh cells that have custom components using cellRendererParams
      // since when we are enabling a product out of assortment into assortment,
      // the underline data for the three columns above dont change (e.g. they are kept as null)
      // the grid wont refresh the cell so we force the refresh of the cell
      // and the other case would be the editAction column
      // that does not have a data field bounded that's why we need to refresh the cell
      params.api.refreshCells({
        rowNodes: [params.node],
        columns: ['editAction'],
        force: true,
      });
      // disable is needed as `validateGrid` also references headers
      // eslint-disable-next-line no-use-before-define
      validateGrid();
      calculateAllFinalRosPercentile();
    },
  },
  plannedSpacebreak: {
    field: 'plannedSpacebreakId',
    colId: 'plannedSpacebreakId',
    headerName: tkey('plannedSpacebreak'),
    headerTooltip: tkey('headerTooltips.plannedSpacebreak'),
    width: 130,
    columnGroupShow: 'open',
    cellEditor: 'agRichSelectCellEditor',
    headerClass: 'editable-column-header',
    editable: isPlannedSpacebreakEditable,
    filterValueGetter: params => {
      if (!params.data.plannedSpacebreakId) return '';
      return getSpacebreakNameById(params);
    },
    valueFormatter: params => {
      if (!params.value) return '';

      return getSpacebreakNameById(params);
    },
    valueSetter: params => {
      // Allow copy and paste AOV3-7645
      params.data.plannedSpacebreakId = get(params, 'newValue._id', null);

      setNewRoSOverride(params);
      return true;
    },
    tooltipValueGetter: params => {
      if (!isNewOrSemiProduct(params)) {
        return [tkey('tooltips.onlyNewProductsAllowed')];
      }
      if (!hasSpacebreakKPIs(params)) {
        return [tkey('tooltips.noKpisRerunSwitching')];
      }
      return null;
    },
    cellEditorParams: params => getAvailableSpacebreaksForProduct(params),
    cellClassRules: {
      'diff-background': hasDiff,
      'disabled-cell': params => !isPlannedSpacebreakEditable(params),
    },
  },
  rateOfSale: {
    field: 'rateOfSale',
    colId: 'rateOfSale',
    headerName: tkey('expected'),
    headerTooltip: tkey('headerTooltips.measuredModelledRoS'),
    width: 135,
    type: ['numericColumnCustom', 'numericAlignment'], // no need for filterParams.valueFormatter because we're using agNumberColumnFilter
    filter: 'agNumberColumnFilter',
    filterParams: getNumericFilterParams({ allowNegative: true }),
    valueFormatter: roundFormatter,
    valueGetter: params => roundFormatter(params, { returnAsNumber: true }),
    filterValueGetter: params => roundFormatter(params, { returnAsNumber: true }),
    columnGroupShow: 'open',
    cellClassRules: {
      'diff-background': hasDiff,
      'text-disabled': isROSGreyedOut,
    },
  },
  rateOfSaleOverride: {
    field: 'rateOfSaleOverride',
    colId: 'rateOfSaleOverride',
    type: ['numericColumnCustom', 'numericAlignment'],
    columnGroupShow: 'open',
    width: 130,
    editable: isRateOfSaleOverrideEditable,
    headerClass: 'editable-column-header',

    valueFormatter: roundFormatter,
    valueGetter: roundFormatter,
    valueParser: agGridUtils.parsers.numericParser,
    tooltipValueGetter: params => {
      if (!isNewOrSemiProduct(params)) {
        return [tkey('tooltips.onlyNewProductsAllowed')];
      }

      if (get(params, 'data.totalSalesOverride', false)) {
        return [tkey('tooltips.cannotSetWithTotalSales')];
      }
      if (!isRateOfSaleOverrideEditable(params)) {
        return [tkey('tooltips.onlyProductsRequiringModelling')];
      }
      const errors = [];
      if (valueIsNotNumericPermissive(params)) errors.push(t('validationErrors.number'));
      if (valueIsLessOrEqual(stringToNumberIfNumeric(params.value), 0))
        errors.push(t('validationErrors.numberMustBeBiggerThan', [0]));
      // TooltipFeature added to cell if value "exists" (not null and not empty string) https://github.com/ag-grid/ag-grid/blob/v23.2.1/community-modules/core/src/ts/rendering/cellComp.ts#L221
      // TooltipFeature.showTooltip checks with !tooltip if value is showable https://github.com/ag-grid/ag-grid/blob/v23.2.1/community-modules/core/src/ts/widgets/tooltipFeature.ts#L171
      return errors.join('\n');
    },

    filter: 'agNumberColumnFilter',
    filterParams: getNumericFilterParams({ allowNegative: true }),
    filterValueGetter: params => roundFormatter(params, { returnAsNumber: true }),

    headerName: tkey('overridden'),
    headerTooltip: tkey('headerTooltips.overriddenRoS'),

    cellClassRules: {
      'diff-background': hasDiff,
      'invalid-nonempty': params =>
        some([
          valueIsNotNumericPermissive(params) ||
            valueIsLessOrEqual(stringToNumberIfNumeric(params.value), 0),
        ]),
      'disabled-cell': params => !isRateOfSaleOverrideEditable(params),
    },
    onCellValueChanged: async params => {
      calculateAllFinalRosPercentile();
      await calculateClusteredProductsRateOfSaleOverride(params);
    },
  },
  totalSalesOverride: {
    field: 'totalSalesOverride',
    colId: 'totalSalesOverride',
    type: ['numericColumnCustom', 'numericAlignment'],
    hide: !isTotalSalesOverrideEnabled.value,
    columnGroupShow: 'open',
    width: 125,
    headerClass: 'editable-column-header',
    editable: isTotalSalesOverrideEditable,
    valueFormatter: overrideValueFormatter,
    valueGetter: roundFormatter,
    valueParser: agGridUtils.parsers.numericParser,
    valueSetter: params => {
      // do not update anything if there's no change or if we're just changing between empty values (null/undefined/'')
      if (
        params.newValue === params.oldValue ||
        ((params.newValue === '' || isNil(params.newValue)) &&
          (params.oldValue === '' || isNil(params.oldValue)))
      ) {
        return;
      }
      const valueCastToNumber = toNumber(params.newValue);
      // if the new value is empty or not a number, keep it as it
      // otherwise, cast it to a number and set it
      if (isEmpty(params.newValue)) {
        params.data.totalSalesOverride = params.newValue;
      } else if (isNumber(valueCastToNumber)) {
        params.data.totalSalesOverride = valueCastToNumber;
      } else {
        params.data.totalSalesOverride = params.newValue;
      }

      if (isEmpty(params.newValue) || !isNumber(valueCastToNumber)) {
        // if the new value is empty or not a number, set value as is
        params.data.rateOfSaleOverride = params.newValue;
      } else {
        params.data.rateOfSaleOverride = valueCastToNumber;
      }

      if (!isNumber(params.data.totalSalesOverride)) {
        resetTotalSalesOverride(params);
      } else {
        setNewRoSOverride(params);
        calculateAllFinalRosPercentile();
      }

      return true;
    },

    tooltipValueGetter: params => {
      if (!isNewOrSemiProduct(params)) {
        return [tkey('tooltips.onlyNewProductsAllowed')];
      }
      if (!isTotalSalesOverrideEditable(params)) {
        return [tkey('tooltips.noKpisRerunSwitching')];
      }

      const errors = [];
      if (valueIsNotNumericPermissive(params)) errors.push(t('validationErrors.number'));
      if (valueIsLessOrEqual(stringToNumberIfNumeric(params.value), 0))
        errors.push(t('validationErrors.numberMustBeBiggerThan', [0]));
      // TooltipFeature added to cell if value "exists" (not null and not empty string) https://github.com/ag-grid/ag-grid/blob/v23.2.1/community-modules/core/src/ts/rendering/cellComp.ts#L221
      // TooltipFeature.showTooltip checks with !tooltip if value is showable https://github.com/ag-grid/ag-grid/blob/v23.2.1/community-modules/core/src/ts/widgets/tooltipFeature.ts#L171
      return errors.join('\n');
    },
    filter: 'agNumberColumnFilter',
    filterValueGetter: params => {
      return roundFormatter(params, { returnAsNumber: true });
    },
    filterParams: getNumericFilterParams({ allowNegative: true }),

    headerName: tkey('totalSalesOverride'),
    headerTooltip: tkey('headerTooltips.totalSalesOverride'),

    cellClassRules: {
      'diff-background': hasDiff,
      'invalid-nonempty': params =>
        some([
          valueIsNotNumericPermissive(params),
          valueIsLessOrEqual(stringToNumberIfNumeric(params.value), 0),
        ]),
      'disabled-cell': params => !isTotalSalesOverrideEditable(params),
      'text-disabled': isROSGreyedOut || isRowGreyedOut,
    },
  },
  trendApplied: {
    field: 'trendApplied',
    colId: 'trendApplied',
    columnGroupShow: 'open',
    type: ['numericColumnCustom', 'numericAlignment'],
    filter: 'agNumberColumnFilter',
    filterParams: getNumericFilterParams({ allowNegative: true }),
    width: 100,

    editable: isTrendAppliedEditable,
    headerClass: 'editable-column-header',

    tooltipValueGetter: params => {
      if (get(params, 'data.totalSalesOverride', false)) {
        return [tkey('tooltips.cannotSetWithTotalSales')];
      }

      if (!isTrendAppliedEditable(params)) {
        return [tkey('tooltips.cannotSetForProducts')];
      }

      const errors = [];
      if (valueIsNotNumericPermissive(params)) errors.push(t('validationErrors.number'));
      if (valueIsLess(params.value, MIN_TREND_VALUE))
        errors.push(t('validationErrors.numberMustBeBiggerOrEqual', [MIN_TREND_VALUE]));
      return errors.join('\n');
    },

    valueFormatter: ({ value }) => formatTrendApplied(value),
    valueSetter: params => {
      // do not update anything if there's no change or if we're just changing between empty values (null/undefined/'')
      if (
        params.newValue === params.oldValue ||
        ((params.newValue === '' || isNil(params.newValue)) &&
          (params.oldValue === '' || isNil(params.oldValue)))
      ) {
        return;
      }
      params.data.trendApplied = ![null, undefined, ''].includes(params.newValue)
        ? getRoundedPercentage(String(params.newValue).replace(/%/g, ''), true)
        : null;
      return true;
    },
    valueParser: params => {
      // If the value has a % sign, remove it before parsing
      const cleanValue =
        params.newValue && typeof params.newValue === 'string'
          ? params.newValue.replace(/%/g, '')
          : params.newValue;

      // Parse as number
      const parsedValue = agGridUtils.parsers.numericParser({
        ...params,
        newValue: cleanValue,
      });

      return parsedValue;
    },

    headerName: tkey('trendApplied'),
    headerTooltip: tkey('headerTooltips.trendApplied'),

    cellClassRules: {
      'diff-background': hasDiff,
      'invalid-nonempty': params =>
        some([valueIsNotNumericPermissive(params), valueIsLess(params.value, MIN_TREND_VALUE)]),
      'disabled-cell': params => !isTrendAppliedEditable(params),
    },
    onCellValueChanged: () => {
      calculateAllFinalRosPercentile();
    },
  },
  // Rate of Sale with Trend Applied
  finalRateOfSale: {
    field: 'finalRateOfSale',
    colId: 'finalRateOfSale',
    columnGroupShow: 'open',
    headerName: tkey('finalRateOfSale'),
    headerTooltip: tkey('headerTooltips.finalRoS'),
    type: ['numericColumnCustom', 'numericAlignment'],
    filter: 'agNumberColumnFilter',
    filterParams: getNumericFilterParams({ allowNegative: true }),
    width: 115,
    valueGetter: params => calculateRoS(params),
    valueFormatter: overrideValueFormatter,
    filterValueGetter: params => {
      // Filters don't have a value property
      // This field has a calculated value so we need to do this override
      // in order to filter the correct value
      params.value = calculateRoS(params);

      return roundFormatter(params, { returnAsNumber: true });
    },
    editable: false,
    tooltipValueGetter,
    cellClassRules: {
      'invalid-numeric': params => {
        return some([
          valueIsNotNumericPermissive(params),
          valueIsLess(params.value, MIN_TREND_VALUE),
        ]);
      },
      'text-disabled': !isEditableRow,
    },
  },
  finalRoSPercentile: {
    field: 'finalRoSPercentile',
    colId: 'finalRoSPercentile',
    columnGroupShow: 'open',
    headerName: tkey('finalRoSPercentile'),
    headerTooltip: tkey('headerTooltips.finalRoSPercentile'),
    type: ['numericColumnCustom', 'numericAlignment'],
    filter: 'agNumberColumnFilter',
    filterParams: getNumericFilterParams({ allowNegative: true }),
    width: 115,
    valueGetter: params => state.productsRoSPercentileMap[params.data.productKey],
    valueFormatter: params => percentageFormatter(params.value, 2),
    editable: false,
    cellClassRules: {
      'invalid-numeric': params => {
        return some([valueIsNotNumericPermissive(params)]);
      },
      'text-disabled': !isEditableRow,
    },
  },
  volumeRoS: {
    field: 'volumeRoS',
    colId: 'volumeRoS',
    columnGroupShow: 'open',
    headerName: tkey('volumeRoS'),
    headerTooltip: tkey('headerTooltips.volumeRoS'),
    type: ['numericColumnCustom', 'numericAlignment'],
    filter: 'agNumberColumnFilter',
    filterParams: getNumericFilterParams({ allowNegative: true }),
    width: 120,
    hide: !isVolumeRoSEnabled.value,
    valueGetter: params => {
      return rosUtils.calculateVolumeRoS({
        measuredRos: params.data.rateOfSale,
        rosOverride: params.data.rateOfSaleOverride,
        trendApplied: params.data.trendApplied,
        price: params.data.theoreticalPrice,
      });
    },
    valueFormatter: params => {
      if (isNil(params.value)) {
        return '';
      }
      return roundFormatter(params);
    },
    filterValueGetter: params => {
      return rosUtils.calculateVolumeRoS({
        measuredRos: params.data.rateOfSale,
        rosOverride: params.data.rateOfSaleOverride,
        trendApplied: params.data.trendApplied,
        price: params.data.theoreticalPrice,
      });
    },
    editable: false,
    cellClassRules: { 'text-disabled': !isEditableRow },
  },
  salesConfidenceScorePerformancePeriod: {
    field: 'salesConfidenceScorePerformancePeriod',
    colId: 'salesConfidenceScorePerformancePeriod',
    headerName: tkey('salesConfidenceScore'),
    headerTooltip: tkey('headerTooltips.salesConfidence'),
    width: 80,
    headerClass: 'border-s-thin',
    cellClass: 'border-s-thin text-right',
    resizable: false,
    filter: false,
    menuTabs: [], // hide menu as no action can be performed
    cellRenderer: salesConfidenceScoreRenderer,
  },
  shrinkToSalesRatio: {
    headerName: tkey('shrinkToSalesRatio'),
    headerTooltip: tkey('headerTooltips.shrinkToSalesRatio'),
    colId: 'shrinkToSalesRatio',
    field: 'shrinkToSalesRatio',
    type: ['numericColumnCustom', 'numericAlignment'], // no need for filterParams.valueFormatter because we're using agNumberColumnFilter
    width: 130,
    filter: 'agNumberColumnFilter',
    columnGroupShow: 'open',
    cellClassRules: {
      'diff-background': hasDiff,
      'text-disabled': !isEditableRow,
    },
    valueFormatter: params => {
      if (!params.value) return '-';
      return agGridUtils.formatters.percentageFormatter(params.value);
    },
    valueGetter: params => getRoundedPercentage(get(params.data, params.colDef.colId)),
    filterValueGetter: params => getRoundedPercentage(params.value),
  },
  theoreticalMargin: {
    colId: 'theoreticalMargin',
    field: 'theoreticalMargin',
    headerName: t('lookup.margin'),
    type: ['numericColumnCustom', 'numericAlignment'],
    filter: 'agNumberColumnFilter',
    filterParams: getNumericFilterParams({ allowNegative: true }),
    columnGroupShow: 'open',
    width: 100,
    valueFormatter: ({ value }) => formatNumberWithOneDecimal(value, true),
    filterValueGetter: params => {
      // we calculate the margin but format as a decimal so it can match the formatted value shown to the user
      // and return as a number so it can be filtered correctly
      const margin = formatNumberWithOneDecimal(get(params.data, params.colDef.colId) * 100);
      return numberUtils.stringToNumberIfNumeric(margin);
    },
    cellClassRules: { 'diff-background': hasDiff },
  },
  theoreticalPrice: {
    colId: 'theoreticalPrice',
    field: 'theoreticalPrice',
    headerName: `${t('scenarioInputPage.attributes.price')} (${t('lookup.currency')})`,
    filter: 'agNumberColumnFilter',
    type: 'numericAlignment',
    width: 107,
    columnGroupShow: 'open',
    valueFormatter: params => agGridUtils.formatters.roundFormatter(params),
    valueGetter: params => agGridUtils.formatters.roundFormatter(params, { returnAsNumber: true }),
    filterValueGetter: params =>
      agGridUtils.formatters.roundFormatter(params, { returnAsNumber: true }),
    cellClassRules: { 'diff-background': hasDiff },
  },
  margin: {
    colId: 'margin',
    field: 'margin',
    headerName: tkey('marginIncWasteMarkdown'),
    headerTooltip: tkey('headerTooltips.marginIncWasteMarkdown'),
    type: ['numericColumnCustom', 'numericAlignment'],
    filter: 'agNumberColumnFilter',
    columnGroupShow: 'open',
    filterParams: getNumericFilterParams({ allowNegative: true }),
    width: 113,
    valueFormatter: ({ value }) => formatNumberWithOneDecimal(value, true),
    // Convert a value stored like 0.00525 to the formatted percentage value (without the % sign) for filtering - e.g. 5,25
    filterValueGetter: params => {
      // multiply by 100 because, margin would store 5.25% as 0.00525 but the 'numberWithOneDecimal'
      // format won't automatically multiply by 100.
      const margin = formatNumberWithOneDecimal(get(params.data, params.colDef.colId) * 100);
      return numberUtils.stringToNumberIfNumeric(margin);
    },
    cellClassRules: { 'diff-background': hasDiff },
  },
  price: {
    colId: 'price',
    field: 'price',
    headerName: `${tkey('priceIncMarkdown')} (${t('lookup.currency')})`,
    headerTooltip: tkey('headerTooltips.priceIncMarkdown'),
    filter: 'agNumberColumnFilter',
    type: 'numericAlignment',
    columnGroupShow: 'open',
    width: 137,
    valueFormatter: params => agGridUtils.formatters.roundFormatter(params),
    valueGetter: params => agGridUtils.formatters.roundFormatter(params, { returnAsNumber: true }),
    filterValueGetter: params =>
      agGridUtils.formatters.roundFormatter(params, { returnAsNumber: true }),
    cellClassRules: { 'diff-background': hasDiff },
  },
  totalMarkdownAssortmentOverridePercentage: {
    field: 'totalMarkdownAssortmentOverridePercentage',
    colId: 'totalMarkdownAssortmentOverridePercentage',
    hide: !useMarkdownAndWasteToCalculateMarginAndPrice.value,
    type: ['numericColumnCustom', 'numericAlignment'],
    columnGroupShow: 'open',
    width: 190,
    editable: isMarkdownAndWasteOverrideEditable,
    headerClass: 'editable-column-header',

    valueFormatter: ({ value }) => {
      if (!value) return;

      const numericValue = numberUtils.stringToNumberIfNumeric(value);
      // divide by 100 because the number entered and stored will be the actual percentage - e.g. 5.25 for 5.25%
      return formatNumberWithOneDecimal(numericValue / 100, true);
    },
    valueGetter: roundFormatter,
    valueParser: agGridUtils.parsers.numericParser,

    tooltipValueGetter: params => {
      if (!isMarkdownAndWasteOverrideEditable(params)) {
        return [tkey('tooltips.cannotSetForProducts')];
      }

      const errors = [];
      if (valueIsNotNumericPermissive(params)) errors.push(t('validationErrors.number'));
      // TooltipFeature added to cell if value "exists" (not null and not empty string) https://github.com/ag-grid/ag-grid/blob/v23.2.1/community-modules/core/src/ts/rendering/cellComp.ts#L221
      // TooltipFeature.showTooltip checks with !tooltip if value is showable https://github.com/ag-grid/ag-grid/blob/v23.2.1/community-modules/core/src/ts/widgets/tooltipFeature.ts#L171
      return errors.join('\n');
    },

    filter: 'agNumberColumnFilter',
    filterParams: getNumericFilterParams({ allowNegative: true }),
    filterValueGetter: params => {
      // we calculate the margin but format as a decimal so it can match the formatted value shown to the user
      // and return as a number so it can be filtered correctly
      const margin = formatNumberWithOneDecimal(get(params.data, params.colDef.colId));
      return numberUtils.stringToNumberIfNumeric(margin);
    },

    headerName: `${tkey('overriddenMarkdown')}`,

    cellClassRules: {
      'diff-background': hasDiff,
      'invalid-nonempty': params => some([valueIsNotNumericPermissive(params)]),
      'disabled-cell': params => !isMarkdownAndWasteOverrideEditable(params),
    },
    onCellValueChanged: calculateMarginAndPriceByMarkdownAndWasteOverride,
  },
  wasteAssortmentOverridePercentage: {
    field: 'wasteAssortmentOverridePercentage',
    colId: 'wasteAssortmentOverridePercentage',
    hide: !useMarkdownAndWasteToCalculateMarginAndPrice.value,
    type: ['numericColumnCustom', 'numericAlignment'],
    columnGroupShow: 'open',
    width: 167,
    editable: isMarkdownAndWasteOverrideEditable,
    headerClass: 'editable-column-header',

    valueFormatter: ({ value }) => {
      if (!value) return;

      const numericValue = numberUtils.stringToNumberIfNumeric(value);
      // divide by 100 because the number entered and stored will be the actual percentage - e.g. 5.25 for 5.25%
      return formatNumberWithOneDecimal(numericValue / 100, true);
    },
    valueGetter: roundFormatter,
    valueParser: agGridUtils.parsers.numericParser,

    tooltipValueGetter: params => {
      if (!isMarkdownAndWasteOverrideEditable(params)) {
        return [tkey('tooltips.cannotSetForProducts')];
      }

      const errors = [];
      if (valueIsNotNumericPermissive(params)) errors.push(t('validationErrors.number'));
      // TooltipFeature.showTooltip checks with !tooltip if value is showable https://github.com/ag-grid/ag-grid/blob/v23.2.1/community-modules/core/src/ts/widgets/tooltipFeature.ts#L171
      return errors.join('\n');
    },

    filter: 'agNumberColumnFilter',
    filterParams: getNumericFilterParams({ allowNegative: true }),
    filterValueGetter: params => {
      // we calculate the margin but format as a decimal so it can match the formatted value shown to the user
      // and return as a number so it can be filtered correctly
      const margin = formatNumberWithOneDecimal(get(params.data, params.colDef.colId));
      return numberUtils.stringToNumberIfNumeric(margin);
    },

    headerName: `${tkey('overriddenWaste')}`,

    cellClassRules: {
      'diff-background': hasDiff,
      'invalid-nonempty': params => some([valueIsNotNumericPermissive(params)]),
      'disabled-cell': params => !isMarkdownAndWasteOverrideEditable(params),
    },
    onCellValueChanged: calculateMarginAndPriceByMarkdownAndWasteOverride,
  },
  totalMarkdownAssortmentPercentage: {
    headerName: tkey('totalMarkdownAssortmentPercentage'),
    headerTooltip: tkey('headerTooltips.totalMarkdownAssortmentPercentage'),
    colId: 'totalMarkdownAssortmentPercentage',
    field: 'totalMarkdownAssortmentPercentage',
    type: ['numericColumnCustom', 'numericAlignment'], // no need for filterParams.valueFormatter because we're using agNumberColumnFilter
    width: 130,
    filter: 'agNumberColumnFilter',
    filterParams: getNumericFilterParams({ allowNegative: true }),
    cellClassRules: {
      'diff-background': hasDiff,
      'text-disabled': !isEditableRow,
    },
    valueFormatter: ({ value }) => {
      if (!value) return '-';
      return formatNumberWithOneDecimal(value, true);
    },
    // Convert a value stored like 0.00525 to the formatted percentage value (without the % sign) for filtering - e.g. 5,25
    filterValueGetter: params => {
      // Return null for falsy (including 0) values to ensure the "blank" and "not blank" filters work correctly
      // for cells which display a dash when the value is 0.
      const value = get(params.data, params.colDef.colId);
      if (!value) return null;
      // multiply by 100 because, unlike waste, markdown would store 5.25% as 0.00525 but the 'numberWithOneDecimal'
      // format won't automatically multiply by 100.
      const margin = formatNumberWithOneDecimal(value * 100);
      return numberUtils.stringToNumberIfNumeric(margin);
    },
  },
  wastePercentageAssortment: {
    headerName: tkey('wastePercentageAssortment'),
    headerTooltip: tkey('headerTooltips.wastePercentageAssortment'),
    colId: 'wastePercentageAssortment',
    field: 'wastePercentageAssortment',
    type: ['numericColumnCustom', 'numericAlignment'], // no need for filterParams.valueFormatter because we're using agNumberColumnFilter
    width: 130,
    filter: 'agNumberColumnFilter',
    filterParams: getNumericFilterParams({ allowNegative: true }),
    columnGroupShow: 'open',
    cellClassRules: {
      'diff-background': hasDiff,
      'text-disabled': !isEditableRow,
    },
    valueFormatter: ({ value }) => {
      if (!value) return '-';
      // divide by 100 because, unlike markdown, waste would store 5.25% as 0.525 but the 'percentagesWithOneDecimal'
      // format will automatically multiply by 100.
      return formatNumberWithOneDecimal(value / 100, true);
    },
    // Convert a value stored like 0.525 to the formatted percentage value (without the % sign) for filtering - e.g. 5,25
    filterValueGetter: params => {
      // Return null for falsy (including 0) values to ensure the "blank" and "not blank" filters work correctly
      // for cells which display a dash when the value is 0.
      const value = get(params.data, params.colDef.colId);
      if (!value) return null;

      const margin = formatNumberWithOneDecimal(value);
      return numberUtils.stringToNumberIfNumeric(margin);
    },
  },
  totalSalesAssortment: {
    headerName: `${tkey('totalSales')} ${t('lookup.currency')}`,
    headerTooltip: tkey('headerTooltips.totalSalesAssortment'),
    field: 'totalSalesAssortment',
    colId: 'totalSalesAssortment',
    autoHeaderHeight: true,
    width: 100,
    type: ['numericColumnCustom', 'numericAlignment'], // no need for filterParams.valueFormatter because we're using agNumberColumnFilter
    cellClassRules: { 'text-disabled': !isEditableRow },
    filter: 'agNumberColumnFilter',
    filterParams: getNumericFilterParams({ allowNegative: true }),
    valueFormatter: params => roundFormatter(params, { format: 'number' }),
    valueGetter: params => roundFormatter(params, { returnAsNumber: true, format: 'number' }),
    filterValueGetter: params => roundFormatter(params, { returnAsNumber: true, format: 'number' }),
  },
  totalVolumeAssortment: {
    headerName: tkey('totalVolume'),
    headerTooltip: tkey('headerTooltips.totalVolumeAssortment'),
    field: 'totalVolumeAssortment',
    colId: 'totalVolumeAssortment',
    autoHeaderHeight: true,
    width: 110,
    type: ['numericColumnCustom', 'numericAlignment'], // no need for filterParams.valueFormatter because we're using agNumberColumnFilter
    filter: 'agNumberColumnFilter',
    filterParams: getNumericFilterParams({ allowNegative: true }),
    valueFormatter: params => roundFormatter(params, { format: 'number' }),
    valueGetter: params => roundFormatter(params, { returnAsNumber: true, format: 'number' }),
    filterValueGetter: params => roundFormatter(params, { returnAsNumber: true, format: 'number' }),
    columnGroupShow: 'open',
    cellClassRules: { 'text-disabled': !isEditableRow },
  },
  weeksCount: {
    headerName: tkey('weeksCount'),
    headerTooltip: tkey('headerTooltips.weeksCount'),
    field: 'weeksCount',
    colId: 'weeksCount',
    autoHeaderHeight: true,
    width: 100,
    type: ['numericColumnCustom', 'numericAlignment'], // no need for filterParams.valueFormatter because we're using agNumberColumnFilter
    filter: 'agNumberColumnFilter',
    filterParams: getNumericFilterParams({ allowNegative: false }),
    columnGroupShow: 'open',
    cellClassRules: { 'text-disabled': !isEditableRow },
  },
  regionCount: {
    headerName: tkey('regionCount'),
    headerTooltip: tkey('headerTooltips.regionCount'),
    field: 'regionCount',
    colId: 'regionCount',
    autoHeaderHeight: true,
    width: 105,
    type: ['numericColumnCustom', 'numericAlignment'], // no need for filterParams.valueFormatter because we're using agNumberColumnFilter
    filter: 'agNumberColumnFilter',
    filterParams: getNumericFilterParams({ allowNegative: false }),
    columnGroupShow: 'open',
    cellClassRules: { 'text-disabled': !isEditableRow },
  },
  storeCount: {
    headerName: tkey('storeCount'),
    headerTooltip: tkey('headerTooltips.storeCount'),
    field: 'storeCount',
    colId: 'storeCount',
    autoHeaderHeight: true,
    width: 100,
    type: ['numericColumnCustom', 'numericAlignment'], // no need for filterParams.valueFormatter because we're using agNumberColumnFilter
    filter: 'agNumberColumnFilter',
    filterParams: getNumericFilterParams({ allowNegative: false }),
    columnGroupShow: 'open',
    cellClassRules: { 'text-disabled': !isEditableRow },
  },
  weeksCountAssortment: {
    headerName: tkey('weeksCount'),
    headerTooltip: tkey('headerTooltips.weeksCountAssortment'),
    field: 'weeksCountAssortment',
    colId: 'weeksCountAssortment',
    autoHeaderHeight: true,
    width: 100,
    type: ['numericColumnCustom', 'numericAlignment'], // no need for filterParams.valueFormatter because we're using agNumberColumnFilter
    filter: 'agNumberColumnFilter',
    filterParams: getNumericFilterParams({ allowNegative: false }),
    columnGroupShow: 'open',
    cellClassRules: { 'text-disabled': !isEditableRow },
  },
  regionCountAssortment: {
    headerName: tkey('regionCount'),
    headerTooltip: tkey('headerTooltips.regionCountAssortment'),
    field: 'regionCountAssortment',
    colId: 'regionCountAssortment',
    autoHeaderHeight: true,
    width: 105,
    type: ['numericColumnCustom', 'numericAlignment'], // no need for filterParams.valueFormatter because we're using agNumberColumnFilter
    filter: 'agNumberColumnFilter',
    filterParams: getNumericFilterParams({ allowNegative: false }),
    columnGroupShow: 'open',
    cellClassRules: { 'text-disabled': !isEditableRow },
  },
  storeCountAssortment: {
    headerName: tkey('storeCount'),
    headerTooltip: tkey('headerTooltips.storeCountAssortment'),
    field: 'storeCountAssortment',
    colId: 'storeCountAssortment',
    autoHeaderHeight: true,
    width: 100,
    type: ['numericColumnCustom', 'numericAlignment'], // no need for filterParams.valueFormatter because we're using agNumberColumnFilter
    filter: 'agNumberColumnFilter',
    filterParams: getNumericFilterParams({ allowNegative: false }),
    columnGroupShow: 'open',
    cellClassRules: { 'text-disabled': !isEditableRow },
  },
  currentlyObservedSpacebreak: {
    headerName: tkey('currentlyObservedSpacebreak'),
    headerTooltip: tkey('headerTooltips.currentlyObservedSpacebreak'),
    field: 'currentlyObservedSpacebreak',
    colId: 'currentlyObservedSpacebreak',
    width: 140,
    filter: 'agSetColumnFilter',
    cellClassRules: { 'text-disabled': !isEditableRow },
  },
  previouslyObservedSpacebreak: {
    headerName: tkey('previouslyObservedSpacebreak'),
    headerTooltip: tkey('headerTooltips.previouslyObservedSpacebreak'),
    field: 'previouslyObservedSpacebreak',
    colId: 'previouslyObservedSpacebreak',
    width: 140,
    filter: 'agSetColumnFilter',
    columnGroupShow: 'open',
    cellClassRules: { 'text-disabled': !isEditableRow },
  },
  dateOfObservedSpacebreakChange: {
    headerName: tkey('dateOfObservedSpacebreakChange'),
    headerTooltip: tkey('headerTooltips.dateOfObservedSpacebreakChange'),
    field: 'dateOfObservedSpacebreakChange',
    colId: 'dateOfObservedSpacebreakChange',
    width: 140,
    filter: 'agDateColumnFilter',
    valueFormatter: params => datesFormatter(params, contextStore.getDateFormats.long),
    // filterParams: {valueFormatter: params => datesFormatter(params, contextStore.getDateFormats.long)},
    filterParams: {
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        // Cell date gets automatically parsed into a date+time string that includes local tz offset
        // The value we get is 2020-09-27 so we can just manually parse this into a date without time.
        const cellDate = new Date(cellValue);
        const cellDateAtMidnight = new Date(
          cellDate.getFullYear(),
          cellDate.getMonth(),
          cellDate.getDate()
        );

        return Math.sign(cellDateAtMidnight - filterLocalDateAtMidnight);
      },
    },
    columnGroupShow: 'open',
    cellClassRules: { 'text-disabled': !isEditableRow },
  },
  chainIntroductionDate: {
    headerName: tkey('chainIntroductionDate'),
    headerTooltip: tkey('headerTooltips.chainIntroductionDate'),
    field: 'chainIntroductionDate',
    colId: 'chainIntroductionDate',
    width: 140,
    filter: 'agDateColumnFilter',
    valueFormatter: params => datesFormatter(params, contextStore.getDateFormats.long),
    // filterParams: {valueFormatter: params => datesFormatter(params, contextStore.getDateFormats.long)},
    filterParams: {
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        // Cell date gets automatically parsed into a date+time string that includes local tz offset
        // The value we get is 2020-09-27 so we can just manually parse this into a date without time.
        const cellDate = new Date(cellValue);
        const cellDateAtMidnight = new Date(
          cellDate.getFullYear(),
          cellDate.getMonth(),
          cellDate.getDate()
        );

        return Math.sign(cellDateAtMidnight - filterLocalDateAtMidnight);
      },
    },
    columnGroupShow: 'open',
    cellClassRules: { 'text-disabled': !isEditableRow },
  },
  emptyColumn: {
    // empty column to make expand / collapse button visible for column groups
    colId: 'emptyColumn',
    minWidth: 80,
    maxWidth: 80,
    resizable: false,
    menuTabs: [], // hide menu as no action can be performed
    columnGroupShow: 'closed',
  },
};

const validateGrid = () => {
  if (!gridApi) return;
  gridState.validationErrors = [];
  const validationRules = {};
  Object.keys(headers).forEach(key => {
    const colConfig = headers[key];
    const type = get(colConfig, 'type');
    if (type) {
      validationRules[colConfig.field || key] = type;
    }
  });
  gridApi.forEachNode(node => {
    const { data } = node;
    const validationFields = intersection(Object.keys(data), Object.keys(validationRules));
    validationFields.forEach(key => {
      if (validationRules[key] === 'numericColumnCustom') {
        const currentValue = data[key];
        if ((key !== 'trendApplied' && !currentValue) || isNull(currentValue)) return;
        if (
          (key === 'trendApplied' && isNumber(currentValue) && currentValue < MIN_TREND_VALUE) ||
          (!isNil(currentValue) && !isNumber(currentValue))
        ) {
          gridState.validationErrors.push({
            field: key,
            value: currentValue,
            _id: data._id,
          });
        }
      }
    });
  });
};

const createClusteredColumn = (clusterName, clusterId) => {
  return {
    headerName: clusterName,
    openByDefault: true,
    children: [
      {
        headerName: tkey('rosModelling'),
        columnGroupShow: 'open',
        children: [
          {
            ...headers.salesConfidenceScorePerformancePeriod,
            field: `${clusterId}.salesConfidenceScorePerformancePeriod`,
            colId: `${clusterId}.salesConfidenceScorePerformancePeriod`,
            cellRenderer: params => salesConfidenceScoreRenderer(params, clusterId),
          },
          {
            ...headers.rateOfSale,
            colId: `${clusterId}.rateOfSale`,
            field: `${clusterId}.rateOfSale`,
          },
          {
            ...headers.finalRateOfSale,
            field: `${clusterId}.finalRateOfSale`,
            colId: `${clusterId}.finalRateOfSale`,
            valueGetter: params => {
              return rosUtils.calculateFinalRoS({
                measuredRos: get(params.data, `${clusterId}.rateOfSale`),
                // TODO apply trend and overrides to each cluster
                trendApplied: params.data.trendApplied,
              });
            },
          },
          {
            ...headers.shrinkToSalesRatio,
            colId: `${clusterId}.shrinkToSalesRatio`,
            field: `${clusterId}.shrinkToSalesRatio`,
          },
        ],
      },
      {
        headerName: tkey('markdownAndWaste'),
        columnGroupShow: 'open',
        children: [
          {
            ...headers.totalMarkdownAssortmentPercentage,
            colId: `${clusterId}.totalMarkdownAssortmentPercentage`,
            field: `${clusterId}.totalMarkdownAssortmentPercentage`,
          },
          ...(useMarkdownAndWasteToCalculateMarginAndPrice.value
            ? [
                {
                  ...headers.totalMarkdownAssortmentOverridePercentage,
                  colId: `${clusterId}.totalMarkdownAssortmentOverridePercentage`,
                  field: `${clusterId}.totalMarkdownAssortmentOverridePercentage`,
                },
              ]
            : []),
          {
            ...headers.wastePercentageAssortment,
            colId: `${clusterId}.wastePercentageAssortment`,
            field: `${clusterId}.wastePercentageAssortment`,
          },
          ...(useMarkdownAndWasteToCalculateMarginAndPrice.value
            ? [
                {
                  ...headers.wasteAssortmentOverridePercentage,
                  colId: `${clusterId}.wasteAssortmentOverridePercentage`,
                  field: `${clusterId}.wasteAssortmentOverridePercentage`,
                },
              ]
            : []),
          {
            ...headers.price,
            colId: `${clusterId}.price`,
            field: `${clusterId}.price`,
          },
          {
            ...headers.margin,
            colId: `${clusterId}.margin`,
            field: `${clusterId}.margin`,
          },
        ],
      },
      headers.emptyColumn,
    ],
  };
};

const buildClusteredColumns = () => {
  // Get any clusters that exist for a selected cluster scheme
  const clusters = get(clusteringStore.selectedScheme, 'clusters', []);

  // Use those clusters to initialise the cluster column group that renders on the table
  return clusters.map(({ clusterName, clusterId }) =>
    createClusteredColumn(clusterName, clusterId)
  );
};

const createUnclusteredColumn = () => {
  return {
    headerName: tkey('unclustered'),
    headerClass: 'border-s-md',
    openByDefault: true,
    children: [
      {
        headerName: tkey('rosModelling'),
        columnGroupShow: 'open',
        children: [
          headers.salesConfidenceScorePerformancePeriod,
          headers.rateOfSale,
          headers.trendApplied,
          headers.rateOfSaleOverride,
          headers.totalSalesOverride,
          headers.plannedSpacebreak,
          headers.finalRateOfSale,
          headers.finalRoSPercentile,
          headers.volumeRoS,
          headers.shrinkToSalesRatio,
        ],
      },
      {
        headerName: tkey('kpiStoresForAnalysis'),
        columnGroupShow: 'open',
        children: [headers.weeksCount, headers.regionCount, headers.storeCount],
      },
      {
        headerName: tkey('distributionKpiStoresForAssortment'),
        columnGroupShow: 'open',
        children: [
          ...[
            headers.totalSalesAssortment,
            headers.totalVolumeAssortment,
            headers.weeksCountAssortment,
            headers.regionCountAssortment,
            headers.storeCountAssortment,
          ],
          ...(historicKPIsEnabled.value
            ? [
                headers.currentlyObservedSpacebreak,
                headers.previouslyObservedSpacebreak,
                headers.dateOfObservedSpacebreakChange,
                headers.chainIntroductionDate,
              ]
            : []),
        ],
      },
      {
        headerName: tkey('markdownAndWaste'),
        columnGroupShow: 'open',
        children: [
          headers.totalMarkdownAssortmentPercentage,
          ...(useMarkdownAndWasteToCalculateMarginAndPrice.value
            ? [headers.totalMarkdownAssortmentOverridePercentage]
            : []),
          headers.wastePercentageAssortment,
          ...(useMarkdownAndWasteToCalculateMarginAndPrice.value
            ? [headers.wasteAssortmentOverridePercentage]
            : []),
          headers.price,
          headers.margin,
        ],
      },
      headers.emptyColumn,
    ],
  };
};

const buildCustomAttributesColumns = () => {
  const customAttributes = get(scenariosStore.selectedScenario, 'customAttributes', []);

  return {
    headerName: t('scenarioInputPage.attributes.tableHeaders.customAttributes'),
    children: customAttributes.map(({ name, id }) => {
      return {
        headerName: name,
        colId: `customAttributes.${id}`,
        field: `customAttributes.${id}.value`,
        // hide by default the custom attributes, but still allow user to toggle them back visible
        hide: true,
      };
    }),
  };
};

// Building dynamic column definitions
const columnDefinitions = computed(() => {
  const clusteredColumns = buildClusteredColumns();
  const prodPerfChildren = [createUnclusteredColumn(), ...clusteredColumns];
  const customAttributesColumns = buildCustomAttributesColumns();

  const productChildren = [
    {
      headerName: tkey('coreProduct'),
      openByDefault: true,
      children: [
        headers.clientProductKey,
        headers.combinedUnitAndMeasure,
        headers.theoreticalPrice,
        headers.theoreticalMargin,
        headers.cannGroup,
        headers.assortment,
      ],
    },
  ];

  const cols = [
    headers.completionCheckbox,
    headers.editIcon,
    ...(!isEmpty(productModellingAlertsCriteria.value) ? [headers.productModellingAlerts] : []),
    headers.copiedDecisionsFlag,
    headers.productType,
    headers.productKey,
    headers.itemDescription,
    ...productChildren,
    ...prodPerfChildren,
    customAttributesColumns,
  ];

  // Apply filters to the columns, if they exist.
  const colDefs = modellingUtils.applyColumnFilters(cols, modellingMainPageAllowedColumns.value);
  return colDefs;
});

const toggleInvalidRows = () => {
  state.showInvalidRows = !state.showInvalidRows;
  gridApi.onFilterChanged();
};

const toggleWarningRows = () => {
  state.showWarningRows = !state.showWarningRows;
  gridApi.onFilterChanged();
};

const checkRowForErrors = row => {
  if (
    !row.assortment &&
    !row.isNewProduct &&
    row.productKeyDisplay &&
    isUndefined(row.isToolGenerated)
  )
    return true;

  const isProductMissingSisterProducts = !some(row.sisterProducts, sp => sp.isSelected);

  // If the product is not established and has no sister products selected, mark it as invalid
  if (
    !isProductEstablished(row.modellingType, row.salesConfidenceScorePerformancePeriod) &&
    isProductMissingSisterProducts
  ) {
    state.invalidRows[row.productKey] = true;
  } else {
    delete state.invalidRows[row.productKey];
  }

  // turn off filter if you've removed all invalid rows
  if (isEmpty(state.invalidRows) && state.showInvalidRows) toggleInvalidRows();

  // turn off filter if warnings were removed
  if (isEmpty(state.warningRows) && state.showWarningRows) toggleWarningRows();
};

const getColumnStateDetails = () => {
  const scenarioId = get(scenariosStore.selectedScenario, '_id', null);
  if (!scenarioId) return;

  return {
    namePrefix: OVERVIEW_COLUMN_STATE_PREFIX,
    stateId: scenarioId,
    version: OVERVIEW_COLUMN_STATE_VERSION,
  };
};

// Load column state from localStorage with scenario ID
const loadColumnState = () => {
  const config = getColumnStateDetails();
  const savedState = gridPreferences.loadColumnState(config);
  if (savedState) {
    const currentState = gridApi.getColumnDefs();
    // Merge saved state with current state to handle new columns
    const mergedState = gridPreferences.mergeColumnStates(savedState, currentState);
    gridApi.setGridOption('columnDefs', mergedState);
  }
};

// Initial data loading and grid setup
const init = async () => {
  if (gridApi) gridState.isGridLoading = true;
  const [baseProductData, cannGroups] = await Promise.all([
    fetchData(),
    scenariosStore.fetchScenarioCannGroups(),
  ]);
  const { existingProductsWithClusters, productsWithoutParents } = baseProductData;
  leafCannGroups = keyBy(cannGroupUtils.getLeafNodes(cannGroups), 'key');

  // Existing products are accessed via a computed for getting sister product data
  gridState.existingProducts = existingProductsWithClusters;
  gridState.productsData = productsWithoutParents;

  // Set default selected view based on a feature flag from client config
  let defaultView = showProductModellingForNewProductsOnly.value
    ? 'newProductsOnly'
    : 'nonEstablishedProductsOnly';
  if (defaultModellingViewToAllProducts.value) {
    defaultView = 'allProducts';
  }
  setProductsDataToDisplay(defaultView);
  calculateAllFinalRosPercentile(gridState.productsData);

  // identify errors on load
  each(gridState.productsData, checkRowForErrors);

  // currentStateDiff should always reset when you load new data as there can't be changes anymore
  gridState.currentStateDiff = {};
  // We keep a copy of the current dataset on initial data so we can reset to it easily
  // those initial arrays aren't reactive so we can't use them directly
  initialProductsData = cloneDeep(gridState.productsData);

  // We keep savedState as an object to help us diff changes quickly.
  gridState.initialSavedState = keyBy(initialProductsData, 'productKey');
  gridState.initialKeyIdMap = initialProductsData.reduce((a, c) => set(a, c.productKey, c._id), {});

  // Load saved column state from localStorage
  if (gridApi) {
    // Explicitly push rowData to the grid API to avoid a race condition in AG Grid 33
    // where the v-model watcher silently skips rowData updates that arrive before the
    // grid's internal ready flag is set. Mirrors the pattern used in updateDataAndGrid().
    gridApi.setGridOption('rowData', gridState.productsData);
    gridApi.setGridOption('columnDefs', columnDefinitions.value);
    loadColumnState();
    applyDefaultSort();
  }
  gridState.isGridLoading = false;
};

// Save column state to localStorage with scenario ID
const saveColumnState = () => {
  const config = getColumnStateDetails();
  const columnDefs = gridApi.getColumnDefs();
  gridPreferences.saveColumnState(config, columnDefs);
};

/**
 * Handles the grid initialization and loads saved column state
 * @param {Object} params - AG Grid initialization parameters
 */
const onGridReady = params => {
  gridApi = params.api;
  gridApi.setGridOption('columnDefs', columnDefinitions.value);
  // Expose the grid API on window in dev mode so Playwright tests can call
  // gridApi methods (e.g. ensureColumnVisible) without touching production code.
  setGridApiForTesting('productModelling', params.api);
};

const doesExternalFilterPass = node => {
  const productKeyDisplay = get(node.data, 'productKeyDisplay');
  const includedAssortment = get(node.data, 'assortment');
  const isToolGenerated = get(node.data, 'isToolGenerated', undefined);
  const isNewProduct = get(node.data, 'isNewProduct');
  const modellingType = get(node.data, 'modellingType');
  const salesConfidenceScorePerformancePeriod = get(
    node.data,
    'salesConfidenceScorePerformancePeriod'
  );

  // Hide Products that are not included to Assortment but show if it's a child sister product
  if (
    state.selectedView === 'forAssortment' &&
    !includedAssortment &&
    !isNewProduct &&
    productKeyDisplay &&
    isUndefined(isToolGenerated)
  )
    return false;

  if (state.selectedView === 'newProductsOnly' && !isNewProduct) return false;
  if (
    state.selectedView === 'nonEstablishedProductsOnly' &&
    (isProductEstablished(modellingType, salesConfidenceScorePerformancePeriod) ||
      !includedAssortment)
  ) {
    return false;
  }

  if (state.showInvalidRows) {
    return !!state.invalidRows[node.data.productKey];
  }

  if (state.showWarningRows) {
    return !!state.warningRows[node.data.productKey];
  }

  const itemDescription = get(node.data, 'itemDescription');

  if (!productKeyDisplay || !itemDescription) return true;
  if (isEmpty(state.searchString)) return true;

  const isProductFound = productUtils.searchProductByKeyOrDescription(
    state.searchString,
    productKeyDisplay,
    itemDescription
  );

  if (isProductFound) return true;
};

const isProductModellingJobRunning = computed(() => {
  return scenariosStore.isJobRunning('setupProductModelling');
});

const isSaveDisabled = computed(
  () => !canEditModellingPage || state.isSaving || isProductModellingJobRunning.value
);

const hasCDTRun = computed(() => {
  const cdtStatus = get(
    scenariosStore.selectedScenarioStatus,
    'measuring.cdt.status',
    sectionStatuses.notRun
  );
  return cdtStatus === sectionStatuses.complete;
});

const exportOptions = [
  { name: tkey('exportProductModelling'), value: 'exportProductModelling' },
  { name: tkey('exportSisterProducts'), value: 'exportSisterProducts' },
];

const escapeField = (value, delimiter) => {
  const str = String(value ?? '');
  if (str.includes(delimiter) || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

// Ag-grid gridApi.exportDataAsExcel don't work in this case because we need to export sister product data
// which is not directly on the row level but nested in the sisterProducts object and can be multiple per product.
// So we have to build our own csv export for this.
const exportSisterProducts = () => {
  const delimiter = contextStore.getCsvDelimiter;
  const header = ['Modelled product key', 'Modelled product description', 'Sister product key'];
  const rows = [header.join(delimiter)];

  forEach(gridState.productsData, product => {
    if (isProductEstablished(product.modellingType, product.salesConfidenceScorePerformancePeriod))
      return;

    const sisterProducts = values(product.sisterProducts);
    forEach(sisterProducts, sister => {
      if (!sister.isSelected) return;

      rows.push(
        [product.productKeyDisplay, product.itemDescription, sister.productKeyDisplay]
          .map(v => escapeField(v, delimiter))
          .join(delimiter)
      );
    });
  });

  const csvStr = rows.join('\n');
  const timestamp = dayjs().format(contextStore.getDateFormats.mediumWithTimeAndHyphens);
  const fileName = `Sister-Products-${timestamp}.csv`;
  const blob = new Blob([csvStr], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  link.remove();
};

const onExport = () => {
  const {
    completionCheckbox: { colId: checkboxId },
    productType: { colId: typeId },
  } = headers;

  const processCellCallback = row => {
    const cellProcessors = {
      [checkboxId]: cellParams => cellParams.value || '',
      [typeId]: cellParams => tkey(cellParams.value.translation),
    };

    const processCell = cellProcessors[row.column.colId];

    return processCell ? processCell(row) : row.value;
  };

  const timestamp = dayjs().format(contextStore.getDateFormats.mediumWithTimeAndHyphens);

  gridApi.exportDataAsExcel({
    fileName: `Product-Modelling-Overview-${timestamp}`,
    allColumns: false,
    exportedRows: 'filteredAndSorted',
    freezeColumns: 'pinned',
    freezeRows: 'headersAndPinnedRows',
    rowGroupExpandState: 'match',
    sheetName: 'Overview',
    processCellCallback,
  });
};

const formatUpdatesForSave = () => {
  // We have to update two separate collections, scenario-products and cluster-scenario-products
  // Updates for each cluster are scoped on each product by their clusterId
  // Separate properties based on that

  const clusters = get(clusteringStore.selectedScheme, 'clusters', []);
  const clusterIds = map(clusters, 'clusterId');
  const scenarioProperties = [
    'rateOfSaleOverride',
    'totalSalesOverride',
    'trendApplied',
    'rateOfSale',
    'modellingMarkedComplete',
    'assortment',
    'margin',
    'price',
    'totalMarkdownAssortmentOverridePercentage',
    'wasteAssortmentOverridePercentage',
    'plannedSpacebreakId',
  ];

  return reduce(
    gridState.currentStateDiff,
    (acc, product, productKey) => {
      const emptyStringToNull = (obj, field) => {
        if (obj[field] === '') return null;
        return obj[field];
      };

      const updatesBeforeSave = {
        rateOfSaleOverride(update) {
          return emptyStringToNull(update, 'rateOfSaleOverride');
        },
        totalSalesOverride(update) {
          return emptyStringToNull(update, 'totalSalesOverride');
        },
        totalMarkdownAssortmentOverridePercentage(update) {
          return emptyStringToNull(update, 'totalMarkdownAssortmentOverridePercentage');
        },
        wasteAssortmentOverridePercentage(update) {
          return emptyStringToNull(update, 'wasteAssortmentOverridePercentage');
        },
      };

      const clusterUpdates = pick(product, clusterIds);
      // Each cluster is a separate update, handle that here
      const separatedClusterUpdates = map(clusterUpdates, (update, clusterId) => ({
        clusterId,
        scenarioId: scenariosStore.selectedScenario._id,
        clusterSchemeId: clusteringStore.selectedScheme._id,
        productKey: toNumber(productKey),
        ...(update.rateOfSale && { rateOfSale: update.rateOfSale }),
        ...(update.margin && { margin: update.margin }),
        ...(update.price && { price: update.price }),
        ...(update.totalMarkdownAssortmentOverridePercentage && {
          totalMarkdownAssortmentOverridePercentage:
            updatesBeforeSave.totalMarkdownAssortmentOverridePercentage(update),
        }),
        ...(update.wasteAssortmentOverridePercentage && {
          wasteAssortmentOverridePercentage:
            updatesBeforeSave.wasteAssortmentOverridePercentage(update),
        }),
      }));

      const scenarioProductUpdate = pick(product, scenarioProperties);
      // We need the id for saving on the server
      scenarioProductUpdate._id = gridState.initialKeyIdMap[productKey];
      scenarioProductUpdate.productKey = toNumber(productKey);

      each(updatesBeforeSave, (func, key) => {
        const updatedField = has(scenarioProductUpdate, key);
        if (updatedField) {
          scenarioProductUpdate[key] = func(scenarioProductUpdate);
        }
      });

      // We replace the entire sisterProducts object, so this is constructed from savedState + our changes
      const sisterProducts = values(
        merge({}, gridState.initialSavedState[productKey].sisterProducts, product.sisterProducts)
      ).map(sp => pick(sp, ['productKey', 'isToolGenerated', 'isSelected']));
      // TODO: AOV3-7877 Review if this is needed
      scenarioProductUpdate.sisterProducts = sisterProducts;

      acc.scenarioProductUpdates.push(scenarioProductUpdate);
      acc.clusteredProductUpdates.push(...separatedClusterUpdates);
      return acc;
    },
    {
      scenarioProductUpdates: [],
      clusteredProductUpdates: [],
    }
  );
};

const updateCurrentState = params => {
  const { productKey, field, currentValue } = params;

  const headerDef = get(headers, field);
  if (get(headerDef, 'type') === 'numericColumnCustom' && !isEmpty(currentValue)) {
    if (
      !isNumber(currentValue) ||
      (headerDef.field === 'trendApplied' && isNumber(currentValue) && currentValue <= 0)
    ) {
      return;
    }
  }

  // If the rows been updated, it needs a calculation
  gridState.changedSinceCalculation.add(productKey);

  const path = `${productKey}.${field}`;
  const originalValue = get(gridState.initialSavedState, path);

  // Consider empty string and null/undefined as equivalent
  const valuesAreEqual =
    isEqual(currentValue, originalValue) ||
    (isNil(currentValue) && (isNil(originalValue) || originalValue === '')) ||
    (isNil(originalValue) && (isNil(currentValue) || currentValue === ''));

  const existingUpdate = !isUndefined(get(gridState.currentStateDiff, path));

  if (!valuesAreEqual) {
    if (!gridState.currentStateDiff[productKey]) gridState.currentStateDiff[productKey] = {};
    setWith(gridState.currentStateDiff, path, currentValue, Object);

    return;
  }

  // Check that the values are equal and that an existingUpdate exists. An existingUpdate of null or '' is a valid case
  // due to a removed override being sent through as 'null' or ''.
  if (valuesAreEqual && (existingUpdate || [null, ''].includes(existingUpdate))) {
    // Remove the field from the object if it's back to its old value.
    // Note: This is only triggered on a change so entering 1 in a cell that already contains 1 triggers nothing.
    unsetCurrentStateDataDiff(gridState.currentStateDiff, path);
    if (isEmpty(gridState.currentStateDiff[productKey])) {
      // No more changes on this object - can't need a calculation.
      gridState.changedSinceCalculation.delete(productKey);
      // If there's nothing left in the object, remove it.
      delete gridState.currentStateDiff[productKey];
    }
  }
};

const getFilteredProductDisplayKeys = () => {
  if (!gridApi) return null;

  const unfilteredKeys = [];
  const filteredKeys = [];

  gridApi.forEachNode(node => {
    unfilteredKeys.push(node.data.productKeyDisplay);
  });

  gridApi.forEachNodeAfterFilter(node => {
    filteredKeys.push(node.data.productKeyDisplay);
  });

  // Same length means no filtering applied
  if (unfilteredKeys.length === filteredKeys.length) return null;

  return filteredKeys;
};

const modelProducts = async () => {
  const payload = {
    scenarioId: scenariosStore.selectedScenario._id,
    typeOfProductsToModel: state.selectedModellingOption,
  };
  await scenarioProductsStore.runSetupProductModelling(payload);
};

const updateDataAndGrid = async () => {
  // Once saved, we pull back the new data and update the grid
  // ag-grid will handle computing the differences between rows but we only pull back top-level products
  // All the expanded sister product state needs to be persisted.
  // To do this, we map over the current grid and replace any of the top-level data, leaving the expanded rows

  // Get all the new data for this page
  const newData = await scenarioProductsStore.fetchScenarioProductsWithClusters();
  const dataWithRowKey = newData.map(p => set(p, 'rowKey', `${p.productKey}`));

  const { freshExistingProducts, productsWithoutParents } = reduce(
    dataWithRowKey,
    (acc, d) => {
      if (!d.parentProduct) acc.productsWithoutParents.push(d);
      if (!d.isNewProduct) acc.freshExistingProducts.push(d);
      return acc;
    },
    { freshExistingProducts: [], productsWithoutParents: [] }
  );

  // AOV3-7013 verify if this is still needed
  // Existing products are accessed via a computed for getting sister product data
  gridState.existingProducts = freshExistingProducts;

  // the new data from the DB is our saved state now - this is what we reset to
  initialProductsData = cloneDeep(productsWithoutParents);
  gridState.initialKeyIdMap = initialProductsData.reduce((a, c) => set(a, c.productKey, c._id), {});

  // merge any changes onto whatever the previous saved state was
  // Will need to pass flat array of changes here to update
  gridState.initialSavedState = keyBy(initialProductsData, 'productKey');

  const updatedRowKeys = new Set(map(dataWithRowKey, 'productKey'));

  gridState.productsData = map(gridState.productsData, product => {
    return updatedRowKeys.has(product.productKey)
      ? merge({}, gridState.initialSavedState[product.productKey])
      : product;
  });

  // Identify errors on load
  each(gridState.productsData, checkRowForErrors);

  // Apply the new data to the grid
  gridApi.setGridOption('rowData', gridState.productsData);

  const modifiedRows = Object.keys(gridState.currentStateDiff).map(key => gridApi.getRowNode(key)); // get the current objects in the table

  // Redraw rows with updated products to see changes
  gridApi.redrawRows({ rowNodes: modifiedRows });
  gridApi.refreshCells({ force: true });

  // We don't have any differences from our saved state now so reset this
  gridState.currentStateDiff = {};
};

const updateDiffFromCopyDecisions = ({ notCopiedSPDecisions = [] }) => {
  const allRemovedDecisions = map(notCopiedSPDecisions, 'productKeyDisplay');

  if (!isEmpty(allRemovedDecisions)) {
    const content = tkey('notCopiedSPDecisions', { notCopiedSPDecisions: allRemovedDecisions });
    snackbarStore.showWarning(content);
  }
  updateDataAndGrid();
};

const discardChanges = () => {
  gridState.isGridLoading = true;

  // Reset to default selected view and revert products data to the originals
  state.selectedView = showProductModellingForNewProductsOnly.value
    ? 'newProductsOnly'
    : 'nonEstablishedProductsOnly';
  gridState.productsData = cloneDeep(initialProductsData);

  // Revert all modifications to sister products
  gridState.currentStateDiff = {};
  state.invalidRows = {};
  gridApi.refreshCells({ columns: ['isSelected'], force: true });

  gridState.isGridLoading = false;
  // identify errors
  each(gridState.productsData, checkRowForErrors);
  calculateAllFinalRosPercentile(gridState.productsData);
};

const saveChanges = async () => {
  const { clusteredProductUpdates, scenarioProductUpdates } = formatUpdatesForSave();

  try {
    gridState.isGridLoading = true;

    // Save changes
    await Promise.all([
      scenarioProductsStore.saveScenarioProducts({
        products: scenarioProductUpdates,
        addedCustomAttributes: [],
        deletedCustomAttributes: [],
        deletedProducts: [],
        scenarioId: scenariosStore.selectedScenario._id,
        dependencyTreeAlreadyTriggered: true,
      }),
      scenarioProductsStore.updateClusteredScenarioProducts(clusteredProductUpdates),
    ]);

    // Re-fetch required products
    await updateDataAndGrid();
    await scenariosStore.loadScenario(scenariosStore.selectedScenario._id);

    snackbarStore.showSuccess(tkey('actions.saveSuccess'));
  } catch (e) {
    console.error(e);
    snackbarStore.showWarning();
  }
  gridState.isGridLoading = false;
};

const refreshGrid = () => {
  gridApi.refreshCells({ force: true });
};

const applyTrend = trendOverride => {
  const updates = [];
  trendOverride = Number(trendOverride);

  gridApi.forEachNodeAfterFilterAndSort(rowNode => {
    const { productKey, totalSalesOverride, assortment } = rowNode.data;

    // If the row has a total sales override or is not included for assortment, skip it
    if (totalSalesOverride || !assortment) return;

    const data = rowNode.data;
    data.trendApplied = trendOverride;
    updateCurrentState({
      productKey,
      field: 'trendApplied',
      currentValue: trendOverride,
    });
    updates.push(data);
  });
  validateGrid(); // TODO pass headers
  gridApi.applyTransaction({ update: updates });
};

const parseParams = params => {
  // Helper function - this can be used to get appropriate info from params for state updates
  const { productKey } = params.data;
  const { field } = params.colDef;
  const currentValue = get(params.data, params.colDef.field);

  return {
    productKey,
    field,
    currentValue,
  };
};

const handleCellEdit = params => {
  // Cell edits come as a specific param structure - we parse this out so we can
  // make updates that have other forms
  const parsedParams = parseParams(params);
  updateCurrentState(parsedParams);
  validateGrid();
};

const shouldShowErrorControls = computed(() => {
  return !notModelledProductsCount.value && !!size(state.invalidRows);
});

const shouldShowWarningControls = computed(() => {
  return !notModelledProductsCount.value && !!size(state.warningRows);
});

const needsCDT = computed(() => {
  return !!size(gridState.productsData) && !hasCDTRun.value;
});

const initSetupProductModelling = async (commit = false) => {
  state.calledFromInitSetupProductModelling = true; // for dependency tree modal

  try {
    // Perform dependency tree check
    const results = await dependencyTreeStore.triggerDependencyTree({
      params: { change: 'productModellingModified', updates: {}, commit },
    });

    if (results.needsFeedback) {
      // We don't need the isAssortmentOnly in the DT modal
      state.dependencyTreeFeedback = omit(results.output, ['isAssortmentOnly']);
      state.dependencyTreeModalOpen = true;
      return;
    }
    await modelProducts();
    await scenariosStore.loadScenario(scenariosStore.selectedScenario._id);
  } catch (e) {
    console.error(e);
  }
};

const setupProductModelling = async typeOfProductsToModel => {
  gridState.isGridLoading = true;
  state.canRunProductSetup = false;
  try {
    state.selectedModellingOption = typeOfProductsToModel.value;
    await initSetupProductModelling();
  } finally {
    gridState.isGridLoading = false;
    state.canRunProductSetup = true;
  }
};

const onSave = async (commit = false) => {
  state.isSaving = true;
  try {
    // Perform assortment only dependency tree check
    const { scenarioProductUpdates: products } = formatUpdatesForSave();

    // Perform dependency tree check
    const results = await dependencyTreeStore.triggerDependencyTree({
      params: {
        change: 'productModellingModified',
        updates: { global: { products } },
        commit,
      },
    });

    if (results.needsFeedback) {
      state.hasOnlyAssortmentFlagModified = results.output.isAssortmentOnly || false;
      // We don't need the isAssortmentOnly in the DT modal
      state.dependencyTreeFeedback = omit(results.output, ['isAssortmentOnly']);
      state.dependencyTreeModalOpen = true;
      return;
    }

    await saveChanges();
  } finally {
    state.isSaving = false;
  }
};

const closeDependencyTreeModal = () => {
  state.dependencyTreeModalOpen = false;
  state.dependencyTreeFeedback = {};
  state.hasOnlyAssortmentFlagModified = false;
  state.calledFromInitSetupProductModelling = false;
};

const isRunProductSetupDisabled = computed(() => {
  return (
    !canEditModellingPage ||
    state.isSaving ||
    !state.canRunProductSetup ||
    isProductModellingJobRunning.value
  );
});

const triggerProceedWithoutDeleteProductModellingModified = async products => {
  try {
    // Always trigger this dependency when product modelling changes are saved.
    // It will only trigger switching matrices and etc if the assortment flag has been changed.
    await dependencyTreeStore.triggerDependencyTree({
      params: {
        change: 'proceedWithoutDeleteProductModellingModified',
        updates: { global: {}, rowLevel: products },
        commit: true,
      },
    });
  } catch (error) {
    console.warn('Failed to run dependency tree:', error);
    // Don't throw - this shouldn't block the save operation
  }
};

const commitDependencyTreeChanges = async () => {
  state.canRunProductSetup = false;

  if (state.calledFromInitSetupProductModelling) await initSetupProductModelling(true);
  else await onSave(true);

  state.canRunProductSetup = true;
  state.dependencyTreeModalOpen = false;
};

const proceedWithoutDependencyTreeChanges = async () => {
  state.canRunProductSetup = false;

  if (state.calledFromInitSetupProductModelling) await modelProducts();
  else {
    state.isSaving = true;
    const { scenarioProductUpdates } = formatUpdatesForSave();
    await saveChanges().finally(() => {
      state.isSaving = false;
    });
    await triggerProceedWithoutDeleteProductModellingModified(scenarioProductUpdates);
  }

  state.canRunProductSetup = true;
  state.dependencyTreeModalOpen = false;
};

// AI Panel Context Builder (called lazily at send-time via contextFactory)
const buildInnerContext = () => {
  const overviewLines = [];

  // Total products
  const totalProducts = gridState.productsData.length;
  overviewLines.push(`**Total Products**: ${totalProducts}`);

  // Modelling status
  const modelledCount = totalProducts - notModelledProductsCount.value;
  overviewLines.push(`**Modelled Products**: ${modelledCount} / ${totalProducts}`);

  if (notModelledProductsCount.value > 0) {
    overviewLines.push(
      `**⚠️ Not Modelled**: ${notModelledProductsCount.value} product${notModelledProductsCount.value !== 1 ? 's' : ''} require modelling`
    );
  }

  // Product type breakdown
  const newProductCount = filter(gridState.productsData, 'isNewProduct').length;
  const establishedCount = establishedProductsCount.value;
  overviewLines.push('\n**Product Types**:');
  overviewLines.push(`  - New Products: ${newProductCount}`);
  overviewLines.push(`  - Established Products: ${establishedCount}`);
  overviewLines.push(`  - Other Products: ${totalProducts - newProductCount - establishedCount}`);

  // Job status
  if (isProductModellingJobRunning.value) {
    overviewLines.push('\n**⚙️ Job Running**: Product modelling in progress');
  }

  // Unsaved changes
  if (!isEmpty(gridState.currentStateDiff)) {
    const changeCount = size(gridState.currentStateDiff);
    overviewLines.push(
      `\n**⚠️ Unsaved Changes**: ${changeCount} product${changeCount !== 1 ? 's' : ''} modified`
    );
  }

  // CDT status
  if (needsCDT.value) {
    overviewLines.push(
      '\n**⚠️ CDT Required**: Category Decision Tree must be run before product modelling'
    );
  }

  const modellingOverview = overviewLines.join('\n');

  // Validation status
  const validationIssues = [];

  if (notModelledProductsCount.value > 0) {
    validationIssues.push(
      `- ${notModelledProductsCount.value} product${notModelledProductsCount.value !== 1 ? 's' : ''} not yet modelled`
    );
  }

  const errorCount = size(gridState.validationErrors);
  if (errorCount > 0) {
    validationIssues.push(`- ${errorCount} validation error${errorCount !== 1 ? 's' : ''}`);
  }

  const invalidRowCount = size(state.invalidRows);
  if (invalidRowCount > 0) {
    validationIssues.push(
      `- ${invalidRowCount} product${invalidRowCount !== 1 ? 's' : ''} with invalid data`
    );
  }

  const warningRowCount = size(state.warningRows);
  if (warningRowCount > 0) {
    validationIssues.push(
      `- ${warningRowCount} product${warningRowCount !== 1 ? 's' : ''} with warnings`
    );
  }

  if (needsCDT.value) {
    validationIssues.push('- Category Decision Tree (CDT) must be run');
  }

  const validationStatus =
    validationIssues.length > 0
      ? `**Validation Issues (${validationIssues.length})**:\n${validationIssues.join('\n')}\n\n⚠️ ${notModelledProductsCount.value > 0 ? 'Cannot proceed until all products are modelled.' : 'Review and resolve issues before saving.'}`
      : '✓ All validation checks passed. Ready to save.';

  // Build context object
  const context = {
    scenarioName: scenariosStore.selectedScenario?.name || 'Unknown',
    scenarioProgress: formatScenarioProgress.value,
    modellingOverview,
    validationStatus,
    canEditPage: canEditModellingPage ? 'yes' : 'no',
    isJobRunning: isProductModellingJobRunning.value ? 'yes' : 'no',
    hasUnsavedChanges: !isEmpty(gridState.currentStateDiff) ? 'yes' : 'no',
  };

  return context;
};

// Lifecycle Hooks
onMounted(async () => {
  aiStore.enableSidePanel({
    section: 'product-modelling',
    contextDisplay: [{ label: 'Product Modelling Overview', value: '' }],
  });
  aiStore.setSidePanelContextFactory(() => buildInnerContext());
  await init();
  // Signal that page data is ready for the agent to act on.
  aiStore.setContextReady(true);
});
// (no onUnmounted AI teardown needed — route watcher in AiSidePanel handles context reset)

// Watchers
watch(
  () => contextStore.getCurrentNumericLocale,
  () => {
    refreshGrid();
  }
);

watch(
  () => contextStore.getCurrentLocale,
  () => {
    refreshGrid();
  }
);

watch(isProductModellingJobRunning, newValue => {
  // Only update the data when the job status switches from running to not running, and grid API is available
  if (newValue || !gridApi) return;
  updateDataAndGrid();
});
</script>

<style lang="scss" scoped>
@import '@style/base/_all.scss';
:deep(.ag-header-cell-text) {
  padding-right: 0.5em;
}
</style>
